/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * MSSQL dialect-native resolver for the MSSQL engine (PRD Slice "MSSQL
 * Resolver — Core").
 *
 * Pipeline (pure-text after GRA-7 Slice A4 — no tree-sitter parse in the
 * resolver hot path):
 *   1. Derive `CompletionContext` from source text (statement isolation +
 *      paren-frame state machine + dotted-base extractor + alias/CTE
 *      bindings).
 *   2. Bail when inside a string or comment.
 *   3. Build `CompletionCandidate[]` from:
 *        - alias-dot column expansion (when the dotted base resolves to a
 *          bound alias),
 *        - schema-qualified table list (when the base resolves to a schema
 *          or `db.schema`),
 *        - position-aware columns (WHERE / SET / INSERT col-list / SELECT
 *          column list / JOIN ON), introspected lazily via
 *          `mssqlColumnCache`,
 *        - cross-schema table candidates from the bus snapshot,
 *        - schema names,
 *        - T-SQL keywords (tier 7).
 *   4. Rank via `rankCandidates`, applying the keyword-casing setting.
 *   5. Translate each `MonacoCompletionItem` to Monaco's runtime shape
 *      (numeric kind enum, range, signature-help command).
 *
 * Pure module — no Monaco/React imports at module-level. The `monaco`
 * runtime instance flows through resolver input.
 */

import type {
  AliasBinding,
  ColumnMeta,
  CompletionCandidate,
  CteBinding,
  DatabaseEngineId,
  FkEdge,
  RoutineMeta,
  SequenceMeta,
  TableMeta,
} from '@/core/engines';
import { ensureMssqlTables } from '@/features/connections/services/language-services/mssqlTableCache';
import { getSnapshot as getSchemaBusSnapshot } from '@/features/intellisense/schemaBus/schemaBus';
import { deriveCompletionContext } from '@/features/intellisense/context/deriveContext';
import { applyGroupByScopeFilter } from '@/features/intellisense/context/groupByScope';
import { inferSubqueryColumns } from '@/features/intellisense/resolver/inferSubqueryColumns';
import {
  applyPivotTransform,
  parsePivotClause,
} from '@/features/intellisense/pivot/parsePivotClause';
import { resolveCteColumns } from '@/features/intellisense/resolver/resolveCteColumns';
import { emitSelectListAliasCandidates } from '@/features/intellisense/resolver/selectListAliases';
import {
  generateFkJoinSnippet,
  makeApplicableFksFinder,
} from '@/features/intellisense/fkJoin/fkJoinSnippet';
import { makeFkEndpointMatcher } from '@/features/intellisense/fkJoin/fkEndpointMatch';
import { noteCompletionIoWait } from '@/features/intellisense/telemetry/providerCost';
import {
  computeBufferReferencedTableKeys,
  computeFkLinkedTableKeys,
  fkLinkedTableKey,
  unionTableKeys,
} from '@/features/intellisense/fkJoin/fkLinkedTableKeys';
import { promoteCaretBindingRelation } from '@/features/intellisense/fkJoin/caretBindingPromotion';
import { rankCandidates, buildRankOptions } from '@/features/intellisense/ranking/rank';
import { emitAliasCandidate } from './aliasSuggestions';
import { enrichCandidatesWithDocs } from './completionDocs';
import { toMonacoItem } from './monacoItem';
import { makeSigilAwareRangeSelector } from './sigilWordRange';

import type {
  OrchestratorResolver,
  OrchestratorResolverInput,
  OrchestratorResolverResult,
} from '../orchestrator/types';

import {
  ensureMssqlColumns,
  getCachedMssqlColumns,
  type MssqlColumnEntry,
} from './mssqlColumnCache';
import {
  findOutputDmlTarget,
  isInsideOutputList,
  isMssqlSystemCatalog,
  scanMssqlBufferTableTypes,
  scanMssqlSyntheticRelations,
  type MssqlMergeOrOutputTarget,
  type MssqlSyntheticRelation,
} from './mssqlExtras';
import { scanBufferCreatedRoutines, isInsideTableDdlStatement } from './ddlStatementScan';
import {
  applyInBufferTableOps,
  findInBufferCreatedTables,
  scanInBufferTableOps,
  type InBufferRename,
  type InBufferTable,
  type InBufferTableOp,
} from '../context/inBufferTables';
import {
  emitInformationSchemaViews,
  emitPerDbBuiltinSchemas,
  emitSysCatalogTables,
  emitSystemCatalogColumns,
  getSystemCatalogColumns,
  isInformationSchema,
  isSysSchema,
  MSSQL_SYSTEM_DATABASE_SET,
} from './mssqlSystemCatalog';
import {
  findEnclosingTriggerHostTable,
  isInsideCatchBlock,
  MSSQL_CATCH_ERROR_FUNCTIONS,
  type MssqlTriggerHostTable,
} from './mssqlProceduralExtras';
import {
  isInsideProceduralBody,
  emitMssqlProceduralLocalCandidates,
} from './mssqlProceduralLocals';
import {
  emitMssqlBatchLocalCandidates,
  hasBatchLocalDeclarations,
} from './mssqlBatchLocals';
import { isAtProceduralStatementStart } from '@/features/intellisense/context/proceduralStatementStart';
import { isAtInsertTargetSlot } from '@/features/intellisense/context/isAtInsertTargetSlot';
import { isAtExecTargetSlot } from '@/features/intellisense/context/isAtExecTargetSlot';
import {
  isAtExecNamedParamSlot,
  type ExecNamedParamSlot,
} from '@/features/intellisense/context/isAtExecNamedParamSlot';
import {
  isAtNextValueForSlot,
  scanBufferCreatedSequences,
} from '@/features/intellisense/context/isAtNextValueForSlot';
import { computeClauseContext } from '@/features/sql-grammar/document/editorContextClause';
import { emitCreateObjectNamespaces, isCreateIndexNameSlot } from './createObjectNamespaces';
import { eqIdentifier, formatIdentifier, formatQualifiedTwoPart, unquoteIdentifier } from './mssqlIdentifiers';
import { findMssqlTable, mssqlDefaultSchemas, type MssqlDefaultSchemas } from './mssqlDefaultSchema';
import {
  emitMergeValuesQualifiedColumns,
  isAtMergeValuesElement,
  partitionMergeBindings,
} from '@/features/intellisense/resolver/mergeValuesQualified';
import { getMssqlKeywordsForPosition, MSSQL_KEYWORDS } from './mssqlKeywords';
import { getMssqlDataTypes, isMssqlDataTypePosition } from './mssqlDataTypes';
import {
  detectMssqlWindowFrameSlot,
  type MssqlWindowFrameSlot,
} from '@/features/sql-grammar/document/editorContextSlot';
import {
  getMssqlConvertStyles,
  getMssqlDateparts,
  isMssqlConvertStylePosition,
  isMssqlDatepartPosition,
} from './mssqlDateparts';
import {
  getMssqlBuiltinFunctions,
  getMssqlGroupByExtensionKeywords,
} from './mssqlBuiltinFunctions';
import { emitValueSlotCandidates } from '../valueSlot/emitValueSlotCandidates';
import { emitPredicateHeadCandidates } from '../predicateHead/emitPredicateHeadCandidates';
import { buildMssqlValueSlotAdapter, collectMssqlScalarRoutineDescriptors } from './mssqlValueSlotAdapter';
import {
  emitResolvedValueCandidates,
  legalValueKeywordsFor,
  type ResolvedValueTarget,
} from '../valueSlot/emitResolvedValueCandidates';
import { coarseTypeOfDataType } from '../valueSlot/types';
import { findInsertValuesTarget } from '../context/findInsertValuesTarget';
import { emitTypeAwareOperators, emitTypeAwareExpressionOperators } from '../operators/emitJsonOperatorCandidates';
import { findPrecedingExpressionType } from '../operators/findPrecedingExpressionType';
import { makeRoutineReturnResolver } from '../operators/routineReturnType';
import {
  emitJsonPathCandidates,
  findJsonPathStringPosition,
} from '../operators/emitJsonPathCandidates';
import { findPrecedingColumnOperand } from '../operators/findPrecedingColumnOperand';
import { emitEnumValueCandidates } from '../operators/emitEnumValueCandidates';
import { computeEnumValueEdit } from '../operators/enumValueInsertion';
import { extractJsonPathFromCursorMssql } from '../operators/extractJsonPathFromCursor.mssql';
import { findValueStringBodyStart } from '../context/comparisonScanHelpers';
import { detectComparisonValueContext } from '../context/detectComparisonValueContext';
import { isAfterCompletedFromRelation } from '../context/isAfterCompletedFromRelation';
import { classifyNextKeywordSlot, type NextKeywordSlotResult } from '../context/nextKeywordSlot';
import { getLegalNextKeywords, isRowLimitSlot } from '../context/legalNextKeywords';
import {
  emitCompoundKeywordRemainders,
  keywordLabelsAlreadyOffered,
} from './compoundKeywordRemainder';
import { buildRowLimitOperandCandidates } from './rowLimitOperand';
import { isAtJoinTargetSlot } from '../context/isAtJoinTargetSlot';
import { isAfterCompletedSelectProjection } from '../context/isAfterCompletedSelectProjection';
import { scanOptionsFor } from '@/features/intellisense/context/slotTextScan';
import { applyParenScopeFilter } from './parenScopeFilter';
import { applyIndexTargetFilter } from './indexTargetFilter';
import { emitIndexNameCandidates } from './indexNameSlot';
import { demoteGeneratedInsertColumns } from './insertableColumnDemotion';
import {
  isPivotForColumnPosition,
  isPivotInListPosition,
} from '@/features/intellisense/context/pivotSlot';
import { filterAlreadyAssignedSetColumns } from './filterAssignedSetColumns';
import { emitAggregateArgHints, emitAggregateTailHints } from './aggregateArgHints';
import { emitProjectionWildcard } from './projectionWildcard';
import { emitSavepointNameCandidates } from '../context/savepointSlot';
import { findTypedInsertColumns } from '../context/findTypedInsertColumns';
import { boundTablesFirst, resolveColumnRefToTable, type RelationResolver } from '../context/resolveColumnRefToTable';
import { getPathHints } from '@/features/sql-resolver/json/pathHints';
import {
  getJsonPathHints,
  isJsonColumnDeclared,
  getLooseRelationships,
} from '@/features/sql-resolver/metaInfoHints/metaInfoHintProvider';
import { emitLooseRelJoinSnippets } from '@/features/intellisense/fkJoin/looseFkJoinSnippet';
import { emitNamedWindowCandidates } from '@/features/intellisense/resolver/inferNamedWindows';
import { applyJoinOnColumnRanking } from '@/features/intellisense/fkJoin/fkColumnRanking';
import {
  applyReferencesTargetPromotion,
  findFkSourceColumnStems,
} from '@/features/intellisense/fkJoin/referencesTargetInference';
import {
  applyJsonFunctionArgPromotion,
  collectBufferJsonFunctionColumns,
  isAtJsonFunctionFirstArg,
} from '@/features/intellisense/fkJoin/jsonFunctionArgAffinity';
import type { LooseRelationship } from '@/types/electron';

const ENGINE: DatabaseEngineId = 'mssql';

export const mssqlResolver: OrchestratorResolver = {
  engineId: ENGINE,
  resolve: async (input) => resolveMssql(input),
};

async function resolveMssql(
  input: OrchestratorResolverInput,
): Promise<OrchestratorResolverResult> {
  const { model, position, monaco } = input;
  const text = model.getValue();
  const offset = model.getOffsetAt(position);

  const scriptScope = input.scriptScope ?? {};
  const ctx = deriveCompletionContext(text, offset, ENGINE, scriptScope);
  const rankOptions = buildRankOptions(text, ctx, offset, input);

  // Script-mode `USE <db>` overrides the connection-default database for
  // snapshot lookup so completion in `USE db1; SELECT * FROM <cursor>` sees
  // db1's tables instead of the tab's connection-default database.
  const effectiveDatabase = scriptScope.currentDatabase ?? input.database;

  const snap = input.connectionId
    ? getSchemaBusSnapshot(input.connectionId, effectiveDatabase)
    : null;

  // Surface regular tables CREATEd earlier in the same buffer so subsequent
  // statements see them and their columns before the schema-bus snapshot
  // refreshes. Temp tables (`#…`) and table variables (`@…`) are excluded —
  // those are owned by `scanMssqlSyntheticRelations` and double-listing them
  // would fight the synth-relation ranking at FROM slots. The normal
  // table-emit + alias-resolution paths pick the merged entries up
  // transparently (flips mssql script 42 §8 — `t.▮` on the in-buffer
  // `acact_OrderTicket`).
  // In-buffer DDL: apply RENAME (sp_rename / ALTER … RENAME) and DROP to the
  // snapshot FIRST, THEN merge in tables CREATEd earlier in the buffer. Order
  // matters: a `DROP TABLE foo; CREATE TABLE foo;` re-create must leave `foo`
  // present (drop the stale DB copy, then re-add the in-buffer one) — applying
  // ops after the create-merge would wrongly drop the just-created table.
  // (Flips script 38 §5 — `acact_ScratchRenamed` after an in-buffer `sp_rename`.)
  const inBufferOps = scanInBufferTableOps(text, offset, 'mssql');
  const inBufferCreated = findInBufferCreatedTables(text, offset, 'mssql');
  const augmentedTables = mergeInBufferTables(
    applyInBufferTableOps(snap?.tables ?? [], inBufferOps, {
      keyOf: (t) => `${(t.schema ?? '').toLowerCase()}.${t.name.toLowerCase()}`,
      keyOfRef: (q, n) => `${(q ?? '').toLowerCase()}.${n.toLowerCase()}`,
      renameTable: (existing, op: InBufferRename) => ({
        name: op.toName,
        ...(op.toQualifier !== null ? { schema: op.toQualifier } : {}),
        kind: existing?.kind ?? 'table',
        columns: existing?.columns ?? [],
      }),
    }),
    inBufferCreated,
  );
  // Objects this BUFFER created or renamed above the caret. They are the ones
  // the user just wrote, so at a `schema.▮` caret they lead the schema's
  // otherwise-alphabetical listing (script.38 §5).
  const bufferFreshTableKeys = computeBufferFreshTableKeys(inBufferOps, inBufferCreated);
  // How SQL Server resolves an unqualified name here (default schema, then
  // `dbo`) — the MetaInfo enum / JSON-path collectors read the hints of the
  // table the query actually names.
  const defaultSchemas = mssqlDefaultSchemas(snap?.mssqlDefaultSchema);
  const resolveTable = mssqlTableResolver(augmentedTables, defaultSchemas);

  // JCS Slice C4 — JSON path completion fires even inside a single-quoted
  // string when the string is in a recognised JSON-path position (after
  // `JSON_VALUE(col, `, `JSON_QUERY(col, `, `JSON_MODIFY(col, `,
  // `JSON_PATH_EXISTS(col, `, etc.). Runs BEFORE the string-bail so the
  // cursor inside `JSON_VALUE([payload], '|` still gets candidates.
  if (input.connectionId && effectiveDatabase) {
    const pathHintsCandidates = collectMssqlJsonPathCandidates({
      text,
      offset,
      connectionId: input.connectionId,
      database: effectiveDatabase,
      snapshotTables: augmentedTables,
      aliasBindings: ctx.aliasBindings,
      resolveTable,
    });
    if (pathHintsCandidates.length > 0) {
      const wordInfo = model.getWordUntilPosition(position);
      const range = {
        startLineNumber: position.lineNumber,
        startColumn: wordInfo.startColumn,
        endLineNumber: position.lineNumber,
        endColumn: wordInfo.endColumn,
      };
      const ranked = rankCandidates(pathHintsCandidates, ctx, String(wordInfo.word ?? ''), rankOptions);
      return { suggestions: ranked.map((item) => toMonacoItem(item, monaco, range)) };
    }
  }

  // Phase 3 — enum-value completion. Fires (like JSON-path) BEFORE the
  // string-bail because the cursor sits inside an unclosed string literal at
  // `WHERE status = '|`. Empty cache → 0 candidates → fall through.
  if (input.connectionId && effectiveDatabase) {
    const enumCandidates = collectMssqlEnumValueCandidates({
      text,
      offset,
      connectionId: input.connectionId,
      database: effectiveDatabase,
      snapshotTables: augmentedTables,
      aliasBindings: ctx.aliasBindings,
      resolveTable,
    });
    if (enumCandidates.length > 0) {
      const edit = computeEnumValueEdit(model, position, text, offset);
      const ranked = rankCandidates(enumCandidates, ctx, edit.word, rankOptions);
      return {
        suggestions: ranked.map((item) => toMonacoItem(item, monaco, edit.range)),
      };
    }
  }

  if (ctx.insideStringOrComment) return { suggestions: [] };

  const wordInfo = model.getWordUntilPosition(position);
  const typed = String(wordInfo.word ?? '');
  // Cold cache → warm fire-and-forget. Subsequent keystrokes will see the
  // populated snapshot. Replaces the deleted `connectForLanguageServices`
  // warmup path that used to live in `sqlToolsLanguageServiceProvider.ts`.
  //
  // `!snap` is the whole gate (S7 item 3): a bus snapshot exists from the
  // moment phase 1 publishes, so a keystroke taken while phase 2 is still
  // streaming in columns never re-kicks the fan-out. And because
  // `ensureMssqlTables` no longer expires on a timer, a warm snapshot makes
  // this call a cache read even if it did fire.
  if (!snap && input.connectionId && effectiveDatabase) {
    void ensureMssqlTables({
      connectionId: input.connectionId,
      database: effectiveDatabase,
    }).catch(() => undefined);
  }

  const synthRels = scanMssqlSyntheticRelations(text, offset);
  const dmlTarget = findOutputDmlTarget(text, ctx.statementRange.start, ctx.statementRange.end);
  // Computed even when an enclosing DML target exists: a trigger body's
  // `FROM inserted i` refers to the TRIGGER's host table, not to whatever the
  // body happens to be inserting into. `emitDottedAccess` picks between them.
  const triggerHostTable = findEnclosingTriggerHostTable(text, offset);
  const insideCatch = isInsideCatchBlock(text, offset);

  // Downstream column-introspection (`ensureColumnsForTable(connectionId,
  // database, ...)`) reads `input.database`; rebind it to the effective
  // database so script-mode `USE <db>` propagates without threading the
  // override through every helper.
  const scopedInput =
    effectiveDatabase === input.database ? input : { ...input, database: effectiveDatabase };

  const candidates = await buildCandidates({
    input: scopedInput,
    ctx,
    text,
    snapshotTables: augmentedTables,
    snapshotSchemas: snap?.schemas ?? [],
    snapshotForeignKeys: snap?.foreignKeys ?? [],
    snapshotRoutines: snap?.routines ?? [],
    snapshotSequences: snap?.sequences ?? [],
    looseRels:
      input.connectionId && effectiveDatabase
        ? getLooseRelationships(input.connectionId, effectiveDatabase)
        : [],
    cteBindings: ctx.cteBindings,
    synthRels,
    dmlTarget,
    triggerHostTable,
    insideCatch,
    bufferFreshTableKeys,
    defaultSchemas,
  });

  // `USING (▮)` / `ROLLUP(▮)` / Oracle's `SAMPLE (▮)` admit operands or
  // nothing — never the tables and FK-join snippets the full emission adds.
  const scoped = applyIndexTargetFilter(
    applyParenScopeFilter(candidates, text, offset, ctx.statementRange.start, ENGINE),
    text, offset, ctx.statementRange.start, ENGINE,
  );
  // GROUP BY scope (`context/groupByScope.ts`) — once the statement groups, a
  // bare non-grouped column is Msg 8120 at HAVING / ORDER BY, so the candidate
  // is dropped rather than demoted. Self-gated: HAVING / ORDER BY /
  // post-GROUP-BY select list only, and only when the GROUP BY clause parsed
  // with confidence. Wired at the one place every arm's candidates converge,
  // so the clause arm, the predicate-head arm and the value-slot refusals are
  // covered by one call. Closes mssql script.51 §1 and §4.
  const grouped = applyGroupByScopeFilter(scoped, {
    text,
    offset,
    statementStart: ctx.statementRange.start,
    statementEnd: ctx.statementRange.end,
    engineId: ENGINE,
    positionKind: ctx.positionKind,
  });
  const withDocs = enrichCandidatesWithDocs(grouped, {
    aliasBindings: ctx.aliasBindings,
    snapshotTables: augmentedTables,
    foreignKeys: snap?.foreignKeys ?? [],
    fold: (s) => unquoteIdentifier(s).toLowerCase(),
  });
  const ranked = rankCandidates(withDocs, ctx, typed, rankOptions);

  const range = {
    startLineNumber: position.lineNumber,
    startColumn: wordInfo.startColumn,
    endLineNumber: position.lineNumber,
    endColumn: wordInfo.endColumn,
  };
  // `@local` / `@@spid` completions keep their sigil in the label; Monaco's
  // word-definition excludes the sigil from the typed word, which makes its
  // fuzzy filter drop the item once the user types past the `@`. Widen the
  // replace range over the sigil for sigil-prefixed candidates only.
  const rangeFor = makeSigilAwareRangeSelector(model, position.lineNumber, range);
  const suggestions = ranked.map((item) => toMonacoItem(item, monaco, rangeFor(item)));
  return { suggestions };
}

// Merge in-buffer CREATE TABLE entries with the schema-bus snapshot. Skips
// entries that collide (folded schema.name) with a snapshot table, and
// temp-table / table-variable names (`#…` / `@…`) — those are owned by the
// synthetic-relation scanner and double-listing them would fight its ranking.
/** `schema.name` keys (lower-cased) of the tables the buffer CREATEd or
 *  renamed-to above the caret — see {@link emitTablesInSchema}. */
function computeBufferFreshTableKeys(
  ops: ReadonlyArray<InBufferTableOp>,
  created: ReadonlyArray<InBufferTable>,
): ReadonlySet<string> {
  const keys = new Set<string>();
  const add = (qualifier: string | null | undefined, name: string): void => {
    keys.add(`${(qualifier ?? '').toLowerCase()}.${name.toLowerCase()}`);
  };
  for (const op of ops) {
    if (op.kind === 'rename') add(op.toQualifier, op.toName);
  }
  for (const t of created) add(t.qualifier, t.name);
  return keys;
}

function mergeInBufferTables(
  snapshot: BuildArgs['snapshotTables'],
  inBuffer: ReadonlyArray<InBufferTable>,
): BuildArgs['snapshotTables'] {
  if (inBuffer.length === 0) return snapshot;
  const keyOf = (schema: string | null | undefined, name: string): string =>
    `${(schema ?? '').toLowerCase()}.${name.toLowerCase()}`;
  const known = new Set<string>();
  for (const t of snapshot) known.add(keyOf(t.schema, t.name));
  const merged: Array<BuildArgs['snapshotTables'][number]> = [...snapshot];
  for (const t of inBuffer) {
    if (t.name.startsWith('#') || t.name.startsWith('@')) continue;
    const schema = t.qualifier ?? null;
    if (known.has(keyOf(schema, t.name))) continue;
    merged.push({
      name: t.name,
      ...(schema !== null ? { schema } : {}),
      kind: t.isTemporary ? 'temporary-table' : 'table',
      columns: t.columns.map((c) => ({ name: c.name, dataType: c.dataType })),
    });
  }
  return merged;
}

type BuildArgs = {
  readonly input: OrchestratorResolverInput;
  readonly ctx: ReturnType<typeof deriveCompletionContext>;
  readonly text: string;
  readonly snapshotTables: ReadonlyArray<{
    readonly name: string;
    readonly schema?: string;
    readonly database?: string;
    readonly kind: string;
    readonly columns: ReadonlyArray<{
      readonly name: string;
      readonly dataType: string;
      // GRA-8 S3 — the DDL REFERENCES column-list slot ranks the referenced
      // table's PK first; the bus snapshot carries this flag per column.
      readonly isPrimaryKey?: boolean;
      readonly jsonClassification?: import('@/features/sql-resolver/types/schema').JsonClassification;
      // Carried by the bus snapshot's ColumnMeta (T18b, D13) — read to build
      // the NULL keyword legality of a typed INSERT VALUES element.
      readonly nullable?: boolean;
      // Generated-value flags (`sys.columns.is_identity` / `is_computed`) —
      // read to demote the columns T-SQL refuses inside an INSERT column
      // list. Undefined when the snapshot predates the fetch.
      readonly isIdentity?: boolean;
      readonly isComputed?: boolean;
    }>;
    // Indexes on the relation — the index-NAME vocabulary of
    // `DROP INDEX ▮ ON t`.
    readonly indexes?: ReadonlyArray<{
      readonly name: string;
      readonly columns: readonly string[];
      readonly isUnique: boolean;
      readonly isPrimary: boolean;
    }>;
  }>;
  readonly snapshotSchemas: readonly string[];
  readonly snapshotForeignKeys: readonly FkEdge[];
  readonly snapshotRoutines: readonly RoutineMeta[];
  readonly snapshotSequences: readonly SequenceMeta[];
  readonly looseRels: readonly LooseRelationship[];
  readonly cteBindings: readonly CteBinding[];
  readonly synthRels: readonly MssqlSyntheticRelation[];
  readonly dmlTarget: MssqlMergeOrOutputTarget | null;
  readonly triggerHostTable: MssqlTriggerHostTable | null;
  readonly insideCatch: boolean;
  /** `schema.name` keys of the tables this buffer created / renamed above the
   *  caret (see {@link computeBufferFreshTableKeys}). */
  readonly bufferFreshTableKeys: ReadonlySet<string>;
  /** Where an unqualified table name resolves: the user's default schema, then `dbo`. */
  readonly defaultSchemas: MssqlDefaultSchemas;
};

/**
 * Recover the T-SQL PIVOT source alias from the text between the inner view's
 * closing paren and the PIVOT clause's opening paren (`) src PIVOT (`). The
 * alias is the first identifier in that gap that is not `AS` / `PIVOT` /
 * `UNPIVOT` / a NULLS modifier. Returns null for Oracle (no source alias).
 */
function scanPivotSourceAlias(
  text: string,
  fromOffset: number,
  toOffset: number,
): string | null {
  const gap = text.slice(fromOffset, toOffset);
  const reserved = new Set([
    'AS',
    'PIVOT',
    'UNPIVOT',
    'INCLUDE',
    'EXCLUDE',
    'NULLS',
  ]);
  const re = /[A-Za-z_@#][A-Za-z0-9_@#$]*|\[[^\]]*\]|"[^"]*"/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(gap)) !== null) {
    const raw = m[0];
    const bare = raw[0] === '[' || raw[0] === '"' ? raw.slice(1, -1) : raw;
    if (reserved.has(bare.toUpperCase())) continue;
    return bare;
  }
  return null;
}

async function buildCandidates(args: BuildArgs): Promise<CompletionCandidate[]> {
  const { ctx, snapshotTables, snapshotSchemas, snapshotForeignKeys, input } = args;
  const out: CompletionCandidate[] = [];

  // Compound-keyword remainder — when the caret is attached to the second (or
  // third) word of a multi-word keyword (`CURRENT R▮`, `UNBOUNDED P▮`,
  // `NEXT V▮`, `REFRESH ON D▮`, `READS S▮`, `ON DUPLICATE K▮`), offer what is
  // left of that keyword. Runs FIRST so the keyword-only short-circuits below
  // (which return `out` as it stands) carry it; additive, so a caret whose head
  // word is really an identifier keeps everything else it had. See
  // `compoundKeywordRemainder.ts`.
  emitCompoundKeywordRemainders(
    out,
    ENGINE,
    args.text,
    ctx.offset,
    ctx.statementRange.start,
    ctx.positionKind,
  );

  // Position-conditional emission.
  const positionKind = ctx.positionKind;

  // PIVOT interior — when the caret sits inside a `PIVOT (…)` clause, the
  // aggregate-argument and `FOR <col>` slots reference the PRE-pivot projection
  // of the source relation, plus the source alias itself (`( … ) src PIVOT …` —
  // `src` is a valid qualifier inside the clause). Emit those and short-circuit
  // (this runs before dotted-access so the bare interior slot is served).
  // Closes mssql script.7 / script.57 interior checkpoints.
  if (ctx.dottedBase === undefined || ctx.dottedBase.parts.length <= 1) {
    const pivotHost = ctx.aliasBindings.find(
      (b) =>
        b.pivotRange !== undefined &&
        ctx.offset > b.pivotRange.start &&
        ctx.offset < b.pivotRange.end,
    );
    if (pivotHost?.bodyRange) {
      const names = inferSubqueryColumns({
        source: args.text,
        bodyRange: pivotHost.bodyRange,
        engineId: 'mssql',
        snapshotTables: snapshotTables.map((t) => ({
          name: t.name,
          ...(t.schema !== undefined ? { schema: t.schema } : {}),
          columns: (t.columns ?? []).map((c) => ({ name: c.name })),
        })),
        cteBindings: ctx.cteBindings,
      });
      if (names.length > 0) {
        for (const name of names) {
          out.push({
            label: name,
            insertText: name,
            isSnippet: false,
            kind: 'column',
            tier: 1,
            detail: '(pivot)',
          });
        }
        // aliasScope skips the source alias (it is outer-invisible), so recover
        // its name from the gap text between the inner view and the clause.
        // NOT inside the `FOR col IN (…)` list: those entries are the FOR
        // column's categorical VALUES, where a relation name — the source
        // alias included — is never legal (script.7 §4 asserts no relation
        // candidate leaks into the list).
        const srcAlias = isPivotInListPosition(
          args.text, ctx.offset, ctx.statementRange.start, scanOptionsFor(ENGINE),
        )
          ? null
          : scanPivotSourceAlias(
              args.text,
              pivotHost.bodyRange.end,
              pivotHost.pivotRange!.start,
            );
        if (srcAlias) {
          out.push({
            label: srcAlias,
            insertText: srcAlias,
            isSnippet: false,
            kind: 'table',
            tier: 1,
            detail: '(source)',
          });
        }
        return out;
      }
    }
  }

  // The pivoted relation may be a plain table (`FROM orders PIVOT (…)`), which
  // has no subquery body for the block above to infer a projection from. The
  // `FOR ▮` slot there names a column of the bound relation; the clause machine
  // reports it as a FROM tail, so the relation keywords answered it instead.
  if (
    ctx.aliasBindings.length > 0 &&
    isPivotForColumnPosition(args.text, ctx.offset, ctx.statementRange.start, scanOptionsFor(ENGINE))
  ) {
    await emitAllBoundAliasColumns(
      out, ctx.aliasBindings, snapshotTables, ctx.cteBindings, input, args.synthRels, args.defaultSchemas,
    );
    return out;
  }

  // Dotted-access — branch on the dotted base.
  if (positionKind === 'dotted-access' && ctx.dottedBase) {
    await emitDottedAccess(out, args);
    return out;
  }

  // `ROLLBACK TO ▮` / `RELEASE ▮` — a savepoint NAME slot. The name is
  // transaction state the buffer itself declared, so no schema emission can
  // reach it; before this the caret fell to `unknown` and answered with the
  // table list. Shared across all five engines (each spells the statements
  // differently — see `savepointSlot.ts`). Closes sqlite script.50 §9/§10.
  if (emitSavepointNameCandidates(out, args.text, ctx.offset, ctx.statementRange.start, ENGINE)) {
    return out;
  }

  // Index-NAME slot. `DROP INDEX ▮ ON t` names an INDEX, not a relation, but
  // the caret falls to 'unknown' and answered with the table list. Shared
  // recognizer + emitter (`indexNameSlot.ts`); it claims the caret only when
  // the snapshot actually carries index metadata for the engine, so an engine
  // whose adapter has not populated `TableMeta.indexes` yet is unchanged.
  if (emitIndexNameCandidates(out, {
    text: args.text,
    offset: ctx.offset,
    statementStart: ctx.statementRange.start,
    statementEnd: ctx.statementRange.end,
    engine: ENGINE,
    tables: args.snapshotTables,
    format: formatIdentifier,
  })) {
    return out;
  }

  // Value-slot plan T9 — scalar-value branch. Reached ONLY when the engine's
  // capability table (or the T5b test setter) admits the slot's role; LIVE in
  // production as of the T13b flip (`valueSlotCapabilities.ts`). The shared
  // policy owns the candidate set; T-SQL builtin functions stay
  // engine-emitted at their tier.
  if (positionKind === 'scalar-value' && ctx.valueSlot) {
    const { adapter, scope } = buildMssqlValueSlotAdapter({
      defaultSchemas: args.defaultSchemas,
      slot: ctx.valueSlot,
      text: args.text,
      aliasBindings: ctx.aliasBindings,
      cteBindings: ctx.cteBindings,
      snapshotTables,
      snapshotRoutines: args.snapshotRoutines,
      snapshotForeignKeys,
      input,
      // C2 P1-1/P1-2 — virtual-relation fallback: synthetic relations
      // (@table vars / #temp by relation name, OPENJSON by alias — same
      // matches `emitAliasColumns` performs) and sys/INFORMATION_SCHEMA
      // catalog views via `getSystemCatalogColumns`.
      virtualColumnsFor: (binding) => {
        const synth =
          args.synthRels.find(
            (r) => r.name.toLowerCase() === binding.relation.toLowerCase(),
          ) ??
          ((binding.kind === 'subquery' || binding.kind === 'derived')
            ? args.synthRels.find(
                (r) =>
                  r.kind === 'openjson' &&
                  r.name.toLowerCase() === binding.alias.toLowerCase(),
              )
            : undefined);
        if (synth) {
          return synth.columns.map((name) => ({
            name,
            dataType: synth.columnTypes.get(name),
          }));
        }
        return getSystemCatalogColumns(binding.schema, binding.relation)?.map(
          (name) => ({ name }),
        );
      },
    });
    await emitValueSlotCandidates(out, ctx.valueSlot, adapter, scope);
    emitMssqlBuiltinFunctions(out);
    // C2 head-parity, scalar-value twin — the SAME two clause-path
    // enrichments the predicate-head branch replays below were lost HERE by
    // the T13b value-slot flip (the clause path emitted both after this
    // branch's return): OPENJSON `WITH (…) AS alias` columns (the binding is
    // mis-aliased by aliasScope, defeating the adapter's alias-keyed
    // `virtualColumnsFor` match) and the CATCH error-state functions (legal
    // scalar operands at a comparison RHS inside BEGIN CATCH).
    for (const r of visibleSynthRels(args.synthRels, ctx.statementRange)) {
      if (r.kind === 'openjson') emitSyntheticColumns(out, r);
    }
    if (args.insideCatch) {
      emitCatchErrorFunctions(out);
    }
    // `@`-locals are async-discovered (worker-bridged ANTLR scope), so they
    // cannot ride the adapter's sync `extraScalarSources` contract (the T9
    // escape hatch). The clause path's emission below sits AFTER this
    // branch's return — replicate its exact guarded calls here or a live
    // flip drops locals at comparison-RHS in procedural / batch-DECLARE
    // bodies (Scripts 33/35/39/41).
    if (isInsideProceduralBody(args.text, ctx.offset)) {
      await emitMssqlProceduralLocalCandidates(args.text, ctx.offset, out);
    } else if (hasBatchLocalDeclarations(args.text, ctx.offset)) {
      await emitMssqlBatchLocalCandidates(args.text, ctx.offset, out);
    }
    // T13b no-worse parity (F5.3-R, clone of pgsqlResolver's T13a fix) — the
    // clause path surfaces SELECT-list projection aliases at HAVING
    // positions (script.16 §3 pins projection aliases at
    // `HAVING COUNT(*) > ▮`). A comparison-RHS slot that overrode the HAVING
    // clause must keep them. Recompute the clause with the same machine
    // `deriveCompletionContext` used (the slot path drops it); WHERE/JOIN-ON
    // slots never gain aliases — projections aren't computed yet at those
    // points in SQL evaluation order.
    const scalarClause = computeClauseContext(
      args.text, ctx.statementRange.start, ctx.offset, 'mssql',
    )?.clause;
    if (scalarClause === 'having') {
      emitSelectListAliasCandidates(
        out, args.text, ctx.statementRange, ctx.offset, 'mssql', 'having',
        (name) => formatIdentifier(name),
      );
    }
    return out;
  }

  // Predicate-head plan P6a/P8a — predicate-head branch, LIVE in production
  // since the P8a flip (mssql carries all three head roles in
  // `predicateHeadCapabilities.ts`). Reached when the capability gate admits
  // the head's role. Shape-mirrors the scalar-value branch above: shared
  // policy owns the candidate set, T-SQL builtins stay engine-emitted at
  // their existing tier (the PH-D4 builtin exemption — they carry no return
  // metadata to tier against, exactly as the scalar branch ships them).
  if (positionKind === 'predicate-head' && ctx.predicateHead) {
    const { adapter } = buildMssqlValueSlotAdapter({
      defaultSchemas: args.defaultSchemas,
      // No `slot`: PH-D3's no-LHS mode. The overload makes this a distinct
      // signature, so a scalar-value caller cannot reach it by dropping its
      // slot.
      text: args.text,
      aliasBindings: ctx.aliasBindings,
      cteBindings: ctx.cteBindings,
      snapshotTables,
      snapshotRoutines: args.snapshotRoutines,
      snapshotForeignKeys,
      input,
      // C2 P1-1/P1-2 — same virtual-relation fallback the scalar-value
      // branch passes (synthetic relations + sys/INFORMATION_SCHEMA catalog
      // views via `getSystemCatalogColumns`).
      virtualColumnsFor: (binding) => {
        const synth =
          args.synthRels.find(
            (r) => r.name.toLowerCase() === binding.relation.toLowerCase(),
          ) ??
          ((binding.kind === 'subquery' || binding.kind === 'derived')
            ? args.synthRels.find(
                (r) =>
                  r.kind === 'openjson' &&
                  r.name.toLowerCase() === binding.alias.toLowerCase(),
              )
            : undefined);
        if (synth) {
          return synth.columns.map((name) => ({
            name,
            dataType: synth.columnTypes.get(name),
          }));
        }
        return getSystemCatalogColumns(binding.schema, binding.relation)?.map(
          (name) => ({ name }),
        );
      },
    });
    await emitPredicateHeadCandidates(out, { head: ctx.predicateHead }, adapter);
    // `keywordsAlreadyEmitted: false` — this branch never calls
    // `emitKeywords`, so the dedup that assumes CAST/CONVERT/SUM/MAX/etc.
    // already surfaced via the keyword path must be disabled here or those
    // builtin/keyword-overlap names vanish entirely (see the doc at
    // `emitMssqlBuiltinFunctions`).
    emitMssqlBuiltinFunctions(out, false);
    // C2 head-parity — OPENJSON `WITH (…) AS alias` bindings come back from
    // aliasScope tagged with the WRONG alias (the WITH clause sits between
    // the parens and `AS j`, so parseRelationName never reaches it), which
    // defeats the adapter's alias-keyed `virtualColumnsFor` match. The
    // clause path compensates with a direct statement-scoped synth-scan
    // emission; replay it verbatim here or a head in an OPENJSON WITH scope
    // loses the declared columns.
    for (const r of visibleSynthRels(args.synthRels, ctx.statementRange)) {
      if (r.kind === 'openjson') emitSyntheticColumns(out, r);
    }
    // C2 head-parity — the clause path's CATCH-block emission
    // (`if (args.insideCatch) …` below) sits AFTER this branch's return, so
    // a bare head inside BEGIN CATCH … END CATCH would lose the seven
    // error-state functions (they are not in the builtin list either).
    if (args.insideCatch) {
      emitCatchErrorFunctions(out);
    }
    // Post-policy parity (P1-sol F8) — the same guarded async `@`-local
    // emission the scalar-value branch replays: worker-bridged ANTLR scope
    // resolution is async and cannot ride the adapter's sync
    // `extraScalarSources` contract (the T9 escape hatch), so a head inside
    // a procedural/batch-DECLARE body would lose its locals without this.
    if (isInsideProceduralBody(args.text, ctx.offset)) {
      await emitMssqlProceduralLocalCandidates(args.text, ctx.offset, out);
    } else if (hasBatchLocalDeclarations(args.text, ctx.offset)) {
      await emitMssqlBatchLocalCandidates(args.text, ctx.offset, out);
    }
    // Post-policy parity (P1-sol F8) — the clause path surfaces SELECT-list
    // projection aliases at HAVING positions; a head that overrode the
    // HAVING clause must keep them. WHERE/JOIN-ON heads never gain aliases —
    // projections aren't computed yet at those points in SQL evaluation
    // order.
    const headClause = computeClauseContext(
      args.text, ctx.statementRange.start, ctx.offset, 'mssql',
    )?.clause;
    if (headClause === 'having') {
      emitSelectListAliasCandidates(
        out, args.text, ctx.statementRange, ctx.offset, 'mssql', 'having',
        (name) => formatIdentifier(name),
      );
    }
    return out;
  }

  // §4a — named-window completion after `OVER `/`OVER (`. Additive (tier 1,
  // no short-circuit): the WINDOW-clause names rank above the position's
  // generic keyword/column vocabulary, which the pipeline still emits below.
  // SQL Server 2022+ has the `WINDOW w AS (…)` clause (decision 7).
  if (!ctx.insideStringOrComment) {
    emitNamedWindowCandidates({
      out,
      text: args.text,
      offset: ctx.offset,
      statementStart: ctx.statementRange.start,
      statementEnd: ctx.statementRange.end,
      engine: 'mssql',
    });
  }

  // GRA-8 S2 — T-SQL data-type slot dispatch. The fine-grained slot gate in
  // `deriveCompletionContext` (mssql only) refines an `'unknown'` clause result
  // (CREATE SEQUENCE … AS, DECLARE @x, CREATE TABLE column-type) into
  // `positionKind === 'data-type'`. Dispatch the curated type list and short-
  // circuit. This is the seam-routed path; it adds the CREATE-SEQUENCE-AS shape
  // the old `isMssqlDataTypePosition` text-walk never recognised (script.42 §1).
  if (positionKind === 'data-type') {
    emitMssqlDataTypeCandidates(out);
    // A ROUTINE PARAMETER's type slot is the one place where a keyword follows
    // the type: `@ProductCount INT OUTPUT`. The user types the type and then
    // needs OUTPUT, so it belongs in the same list — one tier down, since the
    // type itself comes first. Emitted only inside a parameter list, never at
    // a CREATE TABLE column type or a CAST target, where OUTPUT is illegal.
    // Closes mssql script.33 §3.
    if (isMssqlRoutineParamTypeSlot(args.text, ctx.offset, ctx.statementRange.start)) {
      out.push({
        label: 'OUTPUT',
        insertText: 'OUTPUT',
        isSnippet: false,
        kind: 'keyword',
        tier: 2,
        detail: 'Parameter direction',
      });
    }
    return out;
  }

  // F5.3 Wave-4 — T-SQL data-type slot for the CAST/TRY_CAST/PARSE/CONVERT
  // shapes that live inside a confidently-named clause (e.g. `SELECT CAST(x AS
  // |) …` arrives as `select-list`, not `unknown`), so the slot gate above
  // never fires for them. The narrow text-walk in `isMssqlDataTypePosition`
  // recognises them by surface keywords. When matched, surface the curated type
  // list and short-circuit; column / table / keyword candidates would dilute
  // the expected vocabulary. Closes F5.3 R-bucket gaps script.16 / script.53.
  if (isMssqlDataTypePosition(args.text, ctx.offset, ctx.statementRange.start)) {
    emitMssqlDataTypeCandidates(out);
    return out;
  }

  // GRA-8 S2 — window-frame extent slot (`ROWS|RANGE BETWEEN <extent> [AND
  // <extent>]`). The frame body sits inside `OVER(... ORDER BY ...)`, so the
  // clause machine names it `order-by` and the slot gate above never fires —
  // detect it directly via the clause-independent text scan and emit the
  // directional frame-extent vocabulary, short-circuiting so columns /
  // aggregates / generic keywords do not dilute it. Closes script.52 §5/§6/§9.
  {
    const frameSlot = detectMssqlWindowFrameSlot(
      args.text,
      ctx.statementRange.start,
      ctx.offset,
      'mssql',
    );
    if (frameSlot) {
      emitMssqlWindowFrameKeywords(out, frameSlot);
      return out;
    }
  }

  // GRA-8 S3 — DDL constraint/reference slots. The deriveContext slot gate
  // (mssql only) refines an `'unknown'` clause result inside a CREATE/ALTER
  // TABLE constraint body into the precise cross-engine DDL positionKind. These
  // branches mirror the other 4 resolvers' emission; they short-circuit so
  // columns / tables / generic keywords do not dilute the constraint slot.
  // Closes script.29 §5/§6 + script.30 §3/§4/§6.
  if (
    positionKind === 'ddl-create-object-name'
    // ENGINE DIVERGENCE — a T-SQL INDEX name is never schema-qualified (the
    // index lives in its table's schema), so that one shape keeps the generic
    // fallback rather than a schema list it cannot use.
    && !isCreateIndexNameSlot(args.text.slice(ctx.statementRange.start, ctx.offset))
  ) {
    // `CREATE [OR ALTER] VIEW|TABLE|PROC|… ▮` — the NEW object's name. Only a
    // SCHEMA can precede that name, so emit schemas and nothing else: columns,
    // tables and data-type names are all wrong at a name that does not exist
    // yet (script.32 §1's "must NOT include" column). `dbo` is T-SQL's default
    // schema for a bare name, so it leads the tier.
    emitCreateObjectNamespaces(out, snapshotSchemas, {
      defaultName: 'dbo',
      kind: 'schema',
      detail: 'Schema',
      format: formatIdentifier,
    });
    return out;
  }
  if (positionKind === 'ddl-fk-target') {
    // REFERENCES ▮ — the FK target table-name slot. MSSQL is multi-schema, so
    // emit schemas + 2-part-name tables (not bare table names like SQLite).
    // A FK can only reference a base table, never a view — exclude views.
    const fkTargetTables = snapshotTables.filter(
      (t) => t.kind !== 'view' && t.kind !== 'materialized-view',
    );
    emitTablesAndSchemas(
      out,
      fkTargetTables,
      snapshotSchemas,
      ctx.cteBindings.length === 0 && args.synthRels.length === 0
          ? computeBufferReferencedTableKeys(args.text, fkTargetTables)
          : new Set<string>(),
    );
    // I1 parity (mirrors the dotted path) — `FOREIGN KEY (category_id)
    // REFERENCES ▮`: the FK source-column stems name the likely target table;
    // lift it above the alphabetical listing.
    applyReferencesTargetPromotion(
      out,
      findFkSourceColumnStems(args.text, ctx.offset, ctx.statementRange.start),
    );
    return out;
  }
  if (positionKind === 'ddl-index-column') {
    // `CREATE … INDEX ix ON tbl (▮` / `… INCLUDE (▮` — the indexed table's
    // columns, PK first. Mirrors the pgsql / mysql / oracle / sqlite branches;
    // mssql reaches the slot through its own named-clause override (see
    // `deriveContext.ts`), because T-SQL puts the target after `ON`.
    await emitIndexColumnList(out, args);
    return out;
  }
  if (positionKind === 'ddl-references-column-list') {
    // REFERENCES tbl(▮ — the REFERENCED table's column list, PK first.
    emitReferencesColumnList(out, args);
    return out;
  }
  if (positionKind === 'ddl-alter-table-constraint-column-list') {
    // ALTER TABLE … FK/PK/UNIQUE (▮ — the altered table's live columns.
    await emitAlterTableConstraintColumns(out, args);
    return out;
  }
  if (positionKind === 'ddl-create-table-expression') {
    // CHECK / PK / UNIQUE / FK (▮ inside a CREATE TABLE — the new table does
    // not exist yet, so emit the columns declared so far in the CREATE body.
    emitCreateTableInProgressColumns(out, args);
    return out;
  }

  // F5.3 Wave-4 — T-SQL datepart slot. The first argument of DATEADD /
  // DATEPART / DATEDIFF / DATENAME / DATETRUNC is a curated datepart token
  // (year, dayofyear, weekday, microsecond, …), not a column reference or
  // expression. Surface the datepart vocabulary and short-circuit. Closes
  // F5.3 R-bucket gap class `pos=[unknown] labels=[snake-case-identifier]`
  // entries script.54 §1-§7.
  if (isMssqlDatepartPosition(args.text, ctx.offset, ctx.statementRange.start)) {
    emitMssqlDatepartCandidates(out);
    return out;
  }

  // `CONVERT(<type>, <expr>, ▮)` — the style ordinal. Same shape as the
  // datepart slot above (a closed literal vocabulary in a built-in's argument
  // list) and short-circuited for the same reason: the position classifies as
  // a named clause, so without this the generic emission floods the caret with
  // the FROM relation's columns and there is no route to the ordinal at all.
  // Closes mssql script.53 §3.
  if (isMssqlConvertStylePosition(args.text, ctx.offset, ctx.statementRange.start)) {
    emitMssqlConvertStyleCandidates(out);
    return out;
  }

  // CATCH-block context: surface the seven error-state functions
  // (ERROR_MESSAGE / ERROR_NUMBER / ERROR_LINE / ERROR_PROCEDURE /
  // ERROR_SEVERITY / ERROR_STATE / XACT_STATE) at high tier so they
  // appear ahead of regular keywords. Outside CATCH they are not
  // emitted — that's the contract documented in PRD § 2.9 and verified
  // by the IntellisenseEngine E2E spec.
  // EXEC-target slot (Workstream C, script.41 §2 / script.37 §1). The clause
  // machine has no EXEC arm so `EXEC ▮` fell to the table-emitting fallback —
  // but the only legal completions are stored procedures (system sp_* first,
  // then snapshot user procs) plus schemas to qualify them.
  //
  // The USER's own procedures lead: a person typing EXEC almost always means
  // one of theirs, and ranking the 15 curated `sp_*` above them put the wanted
  // item at rank 15. The system list stays, one tier down.
  if (isAtExecTargetSlot(args.text, ctx.offset, ctx.statementRange.start)) {
    for (const r of args.snapshotRoutines) {
      if (r.kind !== 'procedure') continue;
      const label = r.schema ? `${r.schema}.${r.name}` : r.name;
      out.push({
        label,
        insertText: label,
        isSnippet: false,
        kind: 'routine',
        tier: 1,
        detail: 'Stored procedure',
        filterText: label,
      });
    }
    emitMssqlSystemProcedures(out);
    emitTablesAndSchemas(out, [], snapshotSchemas, new Set<string>());
    return out;
  }

  // NEXT-VALUE-FOR target slot (Workstream C, script.42 §3/§6). The clause
  // machine classifies the position as `from` and emitted tables — but the
  // only legal completion is a SEQUENCE (qualified or via a schema). Buffer-
  // declared sequences (`CREATE SEQUENCE` in the same buffer, not yet
  // executed) rank tier 1 ahead of snapshot sequences.
  if (isAtNextValueForSlot(args.text, ctx.offset, ctx.statementRange.start)) {
    emitQualifiedSequences(out, args.snapshotSequences, args.text);
    emitTablesAndSchemas(out, [], snapshotSchemas, new Set<string>());
    return out;
  }

  // EXEC named-parameter slot (Workstream C, script.37 §3-§5). Once the EXEC
  // target is complete, the caret expects the procedure's PARAMETER NAMES —
  // look up the signature in the snapshot routines and surface every
  // parameter at tier 1. Deliberately non-returning: batch @locals and the
  // generic paths below still emit (arguments can be passed positionally
  // from locals), the params just rank first.
  const execParamSlot = isAtExecNamedParamSlot(
    args.text,
    ctx.offset,
    ctx.statementRange.start,
  );
  if (execParamSlot) {
    emitExecProcParameters(out, args.snapshotRoutines, execParamSlot);
  }

  if (args.insideCatch) {
    emitCatchErrorFunctions(out);
  }

  // INSERT-target sub-slot (Workstream C, script.45 §2/§9). The clause
  // machine collapses everything between INSERT and VALUES into the
  // `'values'` position, but the caret right after `INSERT [INTO]` names a
  // RELATION — emit synthetics (nearest-declared first: the just-created
  // #temp / @tablevar is the likeliest target) + tables + schemas and
  // short-circuit so bound-alias columns from the INSERT's SELECT body
  // don't bury them.
  if (positionKind === 'values') {
    const insertSlot = isAtInsertTargetSlot(
      args.text,
      ctx.offset,
      ctx.statementRange.start,
    );
    if (insertSlot) {
      emitSyntheticRelationsAsCandidates(out, args.synthRels, ctx.statementRange, {
        promoteNearestBefore: ctx.offset,
      });
      emitTablesAndSchemas(
        out,
        snapshotTables,
        snapshotSchemas,
        ctx.cteBindings.length === 0 && args.synthRels.length === 0
          ? computeBufferReferencedTableKeys(args.text, snapshotTables)
          : new Set<string>(),
      );
      emitMssqlDatabases(out, args.input.database);
      if (insertSlot.needsInto) {
        out.push({
          label: 'INTO',
          insertText: 'INTO',
          isSnippet: false,
          kind: 'keyword',
          tier: 1,
          detail: 'SQL keyword',
        });
      }
      return out;
    }
  }

  // T-SQL procedural local-variable completion (ROUND2-C1.1).
  // When the cursor is inside a CREATE/ALTER PROCEDURE/FUNCTION/TRIGGER body,
  // surface the @var declarations visible at the cursor position (tier 1).
  // Runs before the DML position-kind branching so procedural locals are
  // merged with (not instead of) regular schema-based candidates. The call is
  // guarded inside `emitMssqlProceduralLocalCandidates` — it degrades to an
  // empty list when the T-SQL ANTLR bundle is not yet loaded or the parse
  // fails, so DML candidates still flow.
  if (isInsideProceduralBody(args.text, ctx.offset)) {
    await emitMssqlProceduralLocalCandidates(args.text, ctx.offset, out);
  } else if (hasBatchLocalDeclarations(args.text, ctx.offset)) {
    // Batch-level `DECLARE @x` (the sp_executesql pattern) — scalar locals
    // visible at the caret, most-recently-assigned first (script.41 §6-§12).
    // The hasBatchLocalDeclarations pre-gate keeps the ANTLR parse off
    // DECLARE-free buffers.
    await emitMssqlBatchLocalCandidates(args.text, ctx.offset, out);
  }

  // From / join: cross-schema tables + schema names + visible CTE names.
  // Don't emit columns or keywords here because the user is naming a
  // relation. When at least one alias is already bound (i.e. the user has
  // typed `FROM x JOIN ` or similar), additionally emit FK-driven JOIN
  // snippets for each candidate table that has an applicable FK edge
  // to/from a bound relation.
  if (positionKind === 'from') {
    // Sub-position: cursor sitting after a completed relation / alias
    // (`FROM x ` or `FROM x AS y ` or `FROM x JOIN y `). At that spot the
    // next legal tokens are continuation keywords (WHERE / JOIN / ON / AS /
    // ORDER / GROUP / HAVING / UNION / …), not another bare table — a
    // comma would be required first. Emit only keywords so the suggest
    // widget stops offering syntactically-illegal table names.
    const fromSlot = classifyNextKeywordSlot(
      args.text,
      ctx.offset,
      ctx.statementRange.start,
      positionKind,
      ctx.aliasBindings,
      ENGINE,
    );
    if (
      fromSlot &&
      (fromSlot.slot === 'after-from-relation-plain' || fromSlot.slot === 'after-join-target')
    ) {
      // Generated table-alias candidate leads the list (orderHint 0); the
      // continuation keywords shift to orderHint 1+. See aliasSuggestions.ts.
      const aliasEmitted = emitAliasCandidate(out, {
        text: args.text,
        offset: ctx.offset,
        statementStart: ctx.statementRange.start,
        slot: fromSlot,
        aliasBindings: ctx.aliasBindings,
        keywords: MSSQL_KEYWORDS,
      });
      emitLegalNextKeywords(out, fromSlot, aliasEmitted ? 1 : 0);
      return out;
    }
    // Any OTHER slot the classifier names at a `from` position is a boundary
    // where a relation is illegal — a set operator starting a new query branch
    // (`… FROM t UNION ▮`), a shape keyword. Emit its keywords and stop, rather
    // than falling through to the table list below.
    //
    // A slot with an EMPTY list (the word is a construct in some other dialect,
    // e.g. a table legally named `tablesample` in MySQL) counts as DECLINED —
    // it must reach the after-relation keyword fallback below, not the bare
    // relation list, which is a syntax error at a completed relation.
    const fromSlotEmitted = !!fromSlot && emitLegalNextKeywords(out, fromSlot);
    if (fromSlotEmitted) return out;
    // Null-fallback: the classifier can decline an after-relation slot it
    // can't categorize confidently (e.g. delimited-identifier edge cases). When
    // the broader `isAfterCompletedFromRelation` text-walk still says we're past
    // a completed relation, preserve the pre-classify behavior — emit the curated
    // continuation keywords rather than offering syntactically-illegal tables.
    if (!fromSlotEmitted && isAfterCompletedFromRelation(args.text, ctx.offset, ctx.statementRange.start)) {
      emitKeywords(out, MSSQL_NEXT_AFTER_FROM_RELATION);
      return out;
    }
    emitTablesAndSchemas(
      out,
      snapshotTables,
      snapshotSchemas,
      unionTableKeys(
        computeFkLinkedTableKeys(
          ctx.aliasBindings,
          snapshotTables,
          snapshotForeignKeys,
          mssqlBindingResolver(snapshotTables, args.defaultSchemas),
        ),
        ctx.cteBindings.length === 0 && args.synthRels.length === 0
          ? computeBufferReferencedTableKeys(args.text, snapshotTables)
          : new Set<string>(),
      ),
    );
    emitMssqlDatabases(out, args.input.database);
    emitCteCandidates(out, ctx);
    emitSyntheticRelationsAsCandidates(out, args.synthRels, ctx.statementRange);
    // Trigger pseudo-tables. Inside a `CREATE TRIGGER … AS BEGIN … END` body,
    // `inserted` / `deleted` are the two relations the body is written to read
    // (`FROM inserted i LEFT JOIN deleted d`), yet no snapshot contains them —
    // so this caret offered every real table and not the two the position is
    // actually about (script.36 §2/§7/§8). Tier 1: inside a trigger body they
    // outrank the schema's tables.
    if (args.triggerHostTable) {
      emitTriggerPseudoRelations(out, args.triggerHostTable);
    }
    // S6 — the relation already written at the caret's own binding outranks
    // the other tier-1 (FK-linked / buffer-referenced) tables. Runs after the
    // synthetic-relation emission so a #temp / @table-var binding at the caret
    // (tier-2 synth candidate) gets lifted to tier 1 too (mssql 45.§5).
    promoteCaretBindingRelation(out, ctx.aliasBindings, ctx.offset);
    // F5.3 R-bucket — FK-driven JOIN snippets only form a syntactically
    // valid continuation at a JOIN target slot (`… JOIN |` / `… INNER
    // JOIN |` / `… CROSS APPLY |`). At a bare-FROM-list slot
    // (`FROM |x` or `FROM a, |`) the snippet inserts an `ON …` clause
    // that would be a syntax error, and the snippets shove the plain
    // table the user is actually typing down the list. Demote to tier 7
    // there so they stay discoverable via fuzzy match but don't outrank
    // plain tables.
    if (ctx.aliasBindings.length > 0 && snapshotForeignKeys.length > 0) {
      const atJoinTargetSlot = isAtJoinTargetSlot(
        args.text,
        ctx.offset,
        ctx.statementRange.start,
      );
      emitFkJoinSnippets(
        out,
        ctx.aliasBindings,
        snapshotTables,
        snapshotForeignKeys,
        atJoinTargetSlot,
        args.defaultSchemas,
      );
    }
    if (ctx.aliasBindings.length > 0 && args.looseRels.length > 0) {
      emitLooseRelJoinSnippets({
        out,
        bindings: ctx.aliasBindings,
        tables: snapshotTables,
        looseRels: args.looseRels,
        realFks: snapshotForeignKeys,
        engine: ENGINE,
        resolveBinding: mssqlBindingResolver(snapshotTables, args.defaultSchemas),
      });
    }
    return out;
  }

  // Join-on, where, having, set, values, returning, group-by, order-by,
  // select-list, subquery, cte-def: emit columns of bound aliases (lazily
  // introspected) plus keywords. Tables/schemas are still useful at the
  // select-list (e.g. you can write `WHERE col = OtherTable.col` with a
  // sub-select), so we include them at lower tier.
  // `USE ▮` — a DATABASE name is the only thing that can follow, yet this
  // caret used to emit the full 290-candidate flood with no database in it.
  if (isMssqlUseStatementTarget(args.text, ctx.statementRange.start, ctx.offset)) {
    emitMssqlDatabases(out, input.database);
    return out;
  }

  if (positionKind === 'ddl-trigger-event' || positionKind === 'ddl-create-trigger-timing') {
    // The slot seam already named these positions; only the emission was
    // missing, so the resolver flooded at a caret it knew exactly.
    const triggerSlot =
      positionKind === 'ddl-trigger-event' ? 'at-trigger-event' : 'at-trigger-timing';
    if (
      emitLegalNextKeywords(out, {
        slot: triggerSlot,
        relationHasAlias: false,
        projectionIsStar: false,
      })
    ) {
      return out;
    }
  }

  if (
    positionKind === 'join-on' ||
    positionKind === 'where' ||
    positionKind === 'having' ||
    positionKind === 'set' ||
    positionKind === 'values' ||
    positionKind === 'returning' ||
    positionKind === 'group-by' ||
    positionKind === 'order-by' ||
    positionKind === 'select-list' ||
    positionKind === 'subquery' ||
    positionKind === 'cte-def' ||
    positionKind === 'limit-offset' ||
    positionKind === 'unknown'
  ) {
    // T18b — typed VALUES element (Docs/AutocompleteValueSlot-Plan.md). When
    // the cursor sits inside a VALUES element whose target column is
    // positively known (explicit column list; findInsertValuesTarget), the
    // resolved-target policy (T18a) replaces the generic values-position
    // emission below and short-circuits — columns/relations are structurally
    // illegal here. NO reclassification (G23): the position stays
    // positionKind 'values'; this is resolver-side dispatch only.
    if (positionKind === 'values') {
      const hit = emitTypedValuesElement(out, args, ctx);
      if (hit) return out;
    }
    // T19 — OUTPUT list, for every DML kind. The clause machine now names
    // OUTPUT a `returning` clause, but the position can still arrive as
    // 'values' (an INSERT whose OUTPUT precedes its VALUES) or 'set', so the
    // backward OUTPUT-list walk inside the emitter is the real guard and the
    // gate here is deliberately wide.
    if (positionKind === 'returning' || positionKind === 'values' || positionKind === 'set') {
      const hit = emitInsertOutputCandidates(out, args, ctx);
      if (hit) return out;
    }
    // Position-aware continuation-keyword boundaries (`WHERE x = 1 |`,
    // `GROUP BY x |`, `ORDER BY x |`, `… ON a=b |`, `select * |`). When the
    // classifier confidently names one of these keyword-only sub-positions,
    // emit ONLY the legal ordered keywords and short-circuit so columns /
    // tables / generic keywords don't dilute the boundary. The FROM-relation
    // and join-target slots are handled by the `from` arm above, so exclude
    // them here. A `null` classification (e.g. `WHERE |`, `SELECT a, |`) falls
    // through to today's full emission unchanged.
    const kwSlot = classifyNextKeywordSlot(
      args.text,
      ctx.offset,
      ctx.statementRange.start,
      positionKind,
      ctx.aliasBindings,
      ENGINE,
    );
    // Anywhere inside the row-limiting clause (`LIMIT ▮` … `FETCH FIRST 10 ROWS
    // ONLY ▮`) only integers, the clause's own keywords and in-scope integer
    // operands are legal — relations, columns and the general keyword list are
    // a syntax error at every one of those carets. Emit that and stop; the
    // schema emission below must not run.
    if (kwSlot && isRowLimitSlot(kwSlot.slot)) {
      return buildRowLimitOperandCandidates(out, kwSlot.slot, ENGINE);
    }
    if (
      kwSlot &&
      kwSlot.slot !== 'after-from-relation-plain' &&
      kwSlot.slot !== 'after-join-target'
    ) {
      if (emitLegalNextKeywords(out, kwSlot)) return out;
    }
    if (ctx.aliasBindings.length > 0) {
      await emitAllBoundAliasColumns(
        out, ctx.aliasBindings, snapshotTables, ctx.cteBindings, input, args.synthRels, args.defaultSchemas,
      );
      // `MERGE … WHEN NOT MATCHED THEN INSERT (…) VALUES (▮` — the operands
      // come from the USING source, so the qualified `src.id` form is the one
      // that gets written, and a bare name the TARGET also carries is an
      // ambiguous column reference. Shared policy with the pgsql / oracle
      // MERGE arms (D9 mirror); this resolver supplies the discovery.
      await emitMssqlMergeValuesColumns(out, args, ctx, input);
    }
    // OPENJSON aliases come back from aliasScope as a derived binding
    // tagged with the wrong alias (the `WITH(...)` clause sits between
    // the parens and the `AS j`, so parseRelationName never reaches the
    // alias). Surface their columns directly from the statement-scoped
    // synth scan. Also emit the unqualified columns for `inserted` /
    // `deleted` pseudo-table use inside OUTPUT clauses.
    const visibleSynth = visibleSynthRels(args.synthRels, ctx.statementRange);
    for (const r of visibleSynth) {
      if (r.kind === 'openjson') emitSyntheticColumns(out, r);
    }
    // T16: the S0 value-slot relation guard (plan: Docs/AutocompleteValueSlot-Plan.md
    // T3/T4) that used to gate the two emitters below on
    // `!detectComparisonValueContext(...)` is RETIRED — every comparison-value
    // operand in where/having/join-on now classifies via the LIVE scalar-value
    // branch above (comparison-rhs, set-rhs, between-low, between-high), which
    // returns before this expression arm is ever reached.
    //
    // P8a (Docs/AutocompletePredicateHead-Plan.md): bare predicate HEADS
    // (`WHERE ▮`, `HAVING ▮`, `ON ▮`, `AND/OR/NOT ▮` continuations) no longer
    // reach this arm either — they classify 'predicate-head' and return from
    // the LIVE head branch above, where relations are FORBIDDEN_KINDS
    // (`WHERE <table>` is illegal SQL). What still lands here under
    // where/having/join-on is the recognizer's conservative refusals —
    // mid-expression boundaries (`WHERE x ▮`), `WHERE (▮` open-paren heads,
    // CASE WHEN heads, IS-chain territory (PH-D6 v1 gaps), the `WITH
    // (NOLOCK)` `ON ▮` shape (clause `'cte-def'`, not in on-head's override
    // set) — plus the non-predicate positionKinds of this arm
    // (select-list/unknown/…). For those the full relation emission below is
    // unchanged by design (the no-worse invariant; the v1 gaps are recorded
    // in the P10 gaps sweep).
    emitTablesAndSchemas(
      out,
      snapshotTables,
      snapshotSchemas,
      ctx.cteBindings.length === 0 && args.synthRels.length === 0
          ? computeBufferReferencedTableKeys(args.text, snapshotTables)
          : new Set<string>(),
    );
    emitCteCandidates(out, ctx);
    // F5.3-R-bucket: surface SELECT-list projection aliases in HAVING /
    // ORDER-BY positions. MSSQL accepts an `AS`-bound alias as a reference in
    // ORDER-BY (standard SQL). HAVING also surfaces aliases when the spec
    // declares them; the engine itself only resolves aliases in ORDER-BY but
    // tests assert HAVING for completion coverage parity with other engines.
    emitSelectListAliasCandidates(
      out,
      args.text,
      ctx.statementRange,
      ctx.offset,
      'mssql',
      positionKind,
      (name) => formatIdentifier(name),
    );
    // Inside a procedural body at a statement-start boundary (after BEGIN /
    // BEGIN TRY / `;` / …), the next legal token is a new statement, so promote
    // the statement-starter keywords to the top (tier 1, orderHint 0).
    // Otherwise they emit at tier 7 and are buried below the procedural locals
    // + table-variable columns (tier 1) that fill the ~13-row suggest viewport
    // — and a bare local is not a legal first token of a statement anyway.
    const procStmtStart =
      isInsideProceduralBody(args.text, ctx.offset) &&
      isAtProceduralStatementStart(args.text, ctx.offset, ENGINE);
    // CB1.7 — promote FROM/INTO/UNION/EXCEPT/INTERSECT/FOR/OPTION to tier 1
    // only at the "select-list, after a completed projection" sub-position
    // (e.g. `SELECT * `, `SELECT col1, col2 `). The other position kinds in
    // this arm (where / set / values / …) keep the un-promoted emission.
    const afterSelectProjection =
      positionKind === 'select-list' &&
      isAfterCompletedSelectProjection(
        args.text, ctx.offset, ctx.statementRange.start, scanOptionsFor(ENGINE),
      );
    const nextLikely: ReadonlySet<string> = procStmtStart
      ? MSSQL_PROC_STATEMENT_STARTERS
      : afterSelectProjection
        ? MSSQL_NEXT_AFTER_SELECT_PROJECTION
        : new Set<string>();
    emitProjectionWildcard(out, positionKind, ENGINE);
    // At a select-list ITEM HEAD (`SELECT ▮`, `SELECT a, ▮`) the later-clause
    // keywords are a syntax error — they need a completed projection and a
    // FROM first — so they are withheld from the flat keyword dump
    // (script.42 §5). Only at the head: once a projection is complete the
    // promotion above owns the position, and every other position kind in
    // this arm keeps the full list.
    emitKeywords(
      out,
      nextLikely,
      procStmtStart ? 0 : undefined,
      positionKind === 'select-list' && !afterSelectProjection
        ? MSSQL_ILLEGAL_AT_SELECT_ITEM_HEAD
        : undefined,
    );
    // F5.3 R-bucket — surface T-SQL built-in functions (aggregates, window,
    // scalar, date/time, JSON helpers, grouping helpers, …) at tier 8 so
    // power users find them via fuzzy match without polluting the top of
    // the suggestion list. Closes script.46 §2 (GROUPING_ID/GROUPING in
    // select-list function-call position) and script.51 §1–§3 (COUNT/AVG/
    // MIN/SUM/MAX in HAVING expression position). Snapshot routines are
    // empty for MSSQL today (signature help still owned by SSTS), so the
    // dedupe set is just defensive.
    emitMssqlBuiltinFunctions(out);
    // F5.3 R-bucket — GROUP BY extension keywords (`GROUPING SETS`, `ROLLUP`,
    // `CUBE`). Only emitted at `group-by` position because they're syntactic
    // GROUP BY extensions, not generic keywords or functions. Closes
    // script.46 §4 / §12 / §14.
    if (positionKind === 'group-by') {
      emitMssqlGroupByExtensionKeywords(out);
    }
    // F5.3 Wave-4 — two-token null-comparison operators at WHERE/HAVING.
    // `IS NULL` / `IS NOT NULL` aren't in the curated single-keyword list,
    // so emit them directly as tier-1 candidates.
    if (positionKind === 'where' || positionKind === 'having') {
      for (const kw of MSSQL_WHERE_NULL_OPERATOR_KEYWORDS) {
        out.push({
          label: kw,
          insertText: kw,
          isSnippet: false,
          kind: 'keyword',
          tier: 1,
          detail: 'SQL keyword',
        });
      }
    }
    // F5.3 R-bucket — multi-token continuation at limit-offset (`ROWS ONLY`).
    // Closes script.20 §4.
    if (positionKind === 'limit-offset') {
      for (const kw of MSSQL_LIMIT_OFFSET_MULTI_KEYWORDS) {
        out.push({
          label: kw,
          insertText: kw,
          isSnippet: false,
          kind: 'keyword',
          tier: 1,
          detail: 'SQL keyword',
        });
      }
    }
    // script.42 §5 — `NEXT VALUE FOR` is a fixed three-token sequence
    // expression legal in a select list; the word-level keyword list only
    // knows NEXT/VALUE/FOR individually. Tier 7 blends with the regular
    // keyword set — presence, not promotion.
    if (positionKind === 'select-list') {
      out.push({
        label: 'NEXT VALUE FOR',
        insertText: 'NEXT VALUE FOR ',
        isSnippet: false,
        kind: 'keyword',
        tier: 7,
        detail: 'SQL keyword',
      });
    }
    // JCS Slice C3: operator suggestion is gated on the cursor's preceding
    // operand. MSSQL JSON ops (`JSON_VALUE`, `JSON_QUERY`, `ISJSON`, etc.)
    // only surface when the LHS column is JSON-classified (`check-isjson`
    // today; `loose-json-declared` ships when MetaInfo flows through the bus).
    emitAggregateArgHints(out, args.text, ctx.offset, ctx.statementRange.start, ENGINE);
    emitAggregateTailHints(out, args.text, ctx.offset, ctx.statementRange.start, ENGINE);
    emitTypeAwareJsonOperators(out, args);
    // F5.2-A — drop already-typed columns from the next slot inside an
    // INSERT column-list (`INSERT INTO t (a, |)` must not re-offer `a`).
    // A column may be assigned only once in an UPDATE, so the SET list's
    // already-assigned names are illegal at the next slot.
    filterAlreadyAssignedSetColumns(
      out, args.text, ctx.offset, ctx.statementRange.start, ctx.statementRange.end, ENGINE,
    );
    if (positionKind === 'values') {
      filterAlreadyTypedInsertColumns(out, args, ctx);
      // T-SQL refuses an IDENTITY column as an INSERT target unless the
      // session sets IDENTITY_INSERT on, and refuses a computed column
      // outright — so they rank BELOW the insertable columns rather than
      // being hidden (mssql script.10 §1/§2). The OUTPUT list, which wants
      // the identity column at full weight, never reaches here: its own
      // emitter short-circuits above.
      demoteGeneratedInsertColumns(out, {
        text: args.text,
        offset: ctx.offset,
        statementStart: ctx.statementRange.start,
        statementEnd: ctx.statementRange.end,
        engine: ENGINE,
        tables: args.snapshotTables,
      });
    }
    return out;
  }

  // Fallback: keywords + tables.
  emitTablesAndSchemas(
    out,
    snapshotTables,
    snapshotSchemas,
    ctx.cteBindings.length === 0 && args.synthRels.length === 0
          ? computeBufferReferencedTableKeys(args.text, snapshotTables)
          : new Set<string>(),
  );
  emitKeywords(out);
  return out;
}

/**
 * T18b — when the cursor sits inside a VALUES element whose target column is
 * positively known (`findInsertValuesTarget`), resolve the target column in
 * the snapshot (`findSnapshotTable` — the same case-insensitive lookup used
 * by the REFERENCES/ALTER-TABLE target resolution elsewhere in this file) and
 * emit the shared resolved-target policy (T18a) — enum values (tier 1),
 * NULL/DEFAULT keywords (D13), a scalar-subquery snippet, and compat-tiered
 * scalar routines. Returns `false` (caller falls through to the unchanged
 * generic emission) when the element isn't positively typed or the target
 * table/column can't be resolved.
 */
function emitTypedValuesElement(
  out: CompletionCandidate[],
  args: BuildArgs,
  ctx: BuildArgs['ctx'],
): boolean {
  // Unlike the other four engines, NO trigger-body guard here: T-SQL's
  // `inserted`/`deleted` are multi-row pseudo-TABLES, not row scopes — a
  // scalar `inserted.col` is illegal in a VALUES element (only reachable via
  // a subquery, which the policy offers), so columns stay structurally
  // illegal even inside trigger bodies.
  const hit = findInsertValuesTarget(
    args.text,
    ctx.offset,
    ctx.statementRange.start,
    ctx.statementRange.end,
    ENGINE,
  );
  if (!hit) return false;
  const tbl = findSnapshotTable(args, hit.table.schema ?? null, hit.table.name);
  if (!tbl) return false;
  const column = tbl.columns.find((c) => eqIdentifier(c.name, hit.columnName));
  if (!column) return false;

  const columnMeta: ColumnMeta = {
    name: column.name,
    dataType: column.dataType,
    nullable: column.nullable ?? false,
    isPrimaryKey: column.isPrimaryKey ?? false,
    ordinal: 0,
  };

  const connectionId = args.input.connectionId;
  const database = args.input.database;
  const target: ResolvedValueTarget = {
    kind: 'insert-column',
    column: columnMeta,
    coarseType: coarseTypeOfDataType(column.dataType),
    legalSources: { columns: false, keywords: legalValueKeywordsFor(ENGINE, columnMeta) },
    collectEnumValues:
      connectionId && database
        ? () => {
            const tmp: CompletionCandidate[] = [];
            emitEnumValueCandidates(tmp, {
              connectionId,
              database,
              schema: tbl.schema ?? '',
              table: tbl.name,
              column: column.name,
              quote: findValueStringBodyStart(args.text, ctx.offset) === undefined,
              tier: 1,
            });
            return tmp;
          }
        : undefined,
  };

  emitResolvedValueCandidates(out, target, {
    engine: ENGINE,
    collectScalarRoutines: () => collectMssqlScalarRoutineDescriptors(args.snapshotRoutines),
  });
  // Builtin scalar functions (GETDATE(), NEWID(), …) are legal VALUES
  // elements — same builtins-after-policy shape as the scalar-value branch
  // (C3 sol finding 2).
  emitMssqlBuiltinFunctions(out);
  return true;
}

/** The OUTPUT pseudo-tables each statement kind exposes. INSERT sees only the
 *  new row, DELETE only the old one, and UPDATE / MERGE see both. */
const OUTPUT_PSEUDO_TABLES: Record<string, readonly { name: string; detail: string }[]> = {
  insert: [{ name: 'inserted', detail: 'OUTPUT pseudo-table (the inserted row)' }],
  delete: [{ name: 'deleted', detail: 'OUTPUT pseudo-table (the deleted row)' }],
  update: [
    { name: 'inserted', detail: 'OUTPUT pseudo-table (the updated row, after)' },
    { name: 'deleted', detail: 'OUTPUT pseudo-table (the updated row, before)' },
  ],
  merge: [
    { name: 'inserted', detail: 'OUTPUT pseudo-table (the row, after)' },
    { name: 'deleted', detail: 'OUTPUT pseudo-table (the row, before)' },
  ],
};

/**
 * T19 — OUTPUT list. Emits the pseudo-table qualifiers for the statement's kind
 * (tier 1, `kind: 'alias'` — the D8 qualifier convention), the target table's
 * columns qualified with a pseudo-table (tier 1; the snapshot carries them
 * synchronously, same as `emitTypedValuesElement`), MERGE's `$action`, and the
 * T-SQL built-in scalar functions. Returns `false` (generic emission serves the
 * position) when the cursor isn't in an OUTPUT list or the target table can't
 * be resolved.
 *
 * This used to be INSERT-only (the D11 scope line), so `UPDATE t SET … OUTPUT ▮`
 * and `DELETE FROM t OUTPUT ▮` — where `deleted` is the whole point — fell
 * through to the flood with the pseudo-tables absent entirely.
 */
function emitInsertOutputCandidates(
  out: CompletionCandidate[],
  args: BuildArgs,
  ctx: BuildArgs['ctx'],
): boolean {
  const { dmlTarget } = args;
  const pseudoTables = dmlTarget ? OUTPUT_PSEUDO_TABLES[dmlTarget.statementKind] : undefined;
  if (!dmlTarget || !pseudoTables) return false;
  if (
    !isInsideOutputList(args.text, ctx.offset, ctx.statementRange.start, ctx.statementRange.end)
  ) {
    return false;
  }
  const tbl = findSnapshotTable(args, dmlTarget.schema ?? null, dmlTarget.name);
  if (!tbl) return false;
  for (const pseudo of pseudoTables) {
    out.push({
      label: pseudo.name,
      insertText: pseudo.name,
      isSnippet: false,
      kind: 'alias',
      tier: 1,
      detail: pseudo.detail,
    });
  }
  // MERGE's OUTPUT can report which branch fired.
  if (dmlTarget.statementKind === 'merge') {
    out.push({
      label: '$action',
      insertText: '$action',
      isSnippet: false,
      kind: 'column',
      tier: 1,
      detail: "OUTPUT column — 'INSERT' | 'UPDATE' | 'DELETE'",
    });
  }
  const qualifier = pseudoTables[0]!.name;
  for (const c of tbl.columns) {
    // T-SQL requires target-table references in an OUTPUT list to be
    // qualified with the INSERTED/DELETED prefix — a bare column name is a
    // binding error there, so accepting the candidate must insert the
    // qualified form (C3 sol finding 1).
    out.push({
      label: c.name,
      insertText: `${qualifier}.${formatIdentifier(c.name)}`,
      isSnippet: false,
      kind: 'column',
      tier: 1,
      detail: `${c.dataType} (${qualifier})`,
      filterText: c.name,
    });
  }
  emitMssqlBuiltinFunctions(out);
  return true;
}

/** F5.2-A — drop column candidates whose label matches a column already
 *  typed earlier in the same INSERT column-list. No-op outside INSERT
 *  column-list (the helper returns null then). */
function filterAlreadyTypedInsertColumns(
  out: CompletionCandidate[],
  args: BuildArgs,
  ctx: BuildArgs['ctx'],
): void {
  const typed = findTypedInsertColumns(
    args.text,
    ctx.offset,
    ctx.statementRange.start,
    ctx.statementRange.end,
    ENGINE,
  );
  if (!typed || typed.length === 0) return;
  const typedSet = new Set(typed);
  for (let i = out.length - 1; i >= 0; i -= 1) {
    const c = out[i];
    if (c.kind !== 'column') continue;
    if (typedSet.has(c.label.toLowerCase())) {
      out.splice(i, 1);
    }
  }
}

/** `'inserted'` / `'deleted'` when the dotted base names one of the trigger /
 *  OUTPUT pseudo-tables — either directly (`inserted.`) or through the alias a
 *  trigger body almost always gives it (`FROM inserted i` → `i.`). */
export function resolvePseudoTableBase(
  baseLower: string,
  bindings: readonly AliasBinding[],
): 'inserted' | 'deleted' | null {
  if (baseLower === 'inserted' || baseLower === 'deleted') return baseLower;
  const bound = bindings.find((b) => b.alias.toLowerCase() === baseLower);
  const relation = bound?.relation.toLowerCase();
  return relation === 'inserted' || relation === 'deleted' ? relation : null;
}

/**
 * The binding a dotted base names, when more than one carries that alias.
 *
 * T-SQL's `UPDATE p SET p.▮… FROM Production.Product p` binds `p` TWICE: the
 * statement head contributes a degenerate `p -> p` (the target is written as
 * the alias, its relation not yet known) and the later FROM contributes the
 * real `p -> Product`. Taking the first match resolved to a table named `p`,
 * which does not exist, so the position emitted nothing at all — the widget
 * was filled entirely by Monaco's word-based scrape until that was turned off.
 *
 * Prefer a binding whose relation is a table the snapshot actually knows; fall
 * back to the first match so a CTE / derived / synthetic relation (absent from
 * the snapshot by design) still resolves exactly as before.
 */
function pickAliasBinding(
  bindings: readonly AliasBinding[],
  aliasLower: string,
  tables: BuildArgs['snapshotTables'],
): AliasBinding | undefined {
  const matches = bindings.filter((b) => b.alias.toLowerCase() === aliasLower);
  if (matches.length <= 1) return matches[0];
  const known = matches.find((b) =>
    tables.some((t) => t.name.toLowerCase() === b.relation.toLowerCase()),
  );
  return known ?? matches[0];
}

async function emitDottedAccess(out: CompletionCandidate[], args: BuildArgs): Promise<void> {
  const { ctx, snapshotTables, cteBindings, input, synthRels, dmlTarget, triggerHostTable } = args;
  if (!ctx.dottedBase) return;
  const { parts } = ctx.dottedBase;
  if (parts.length === 0) return;
  const baseLower = unquoteIdentifier(parts[0]!).toLowerCase();

  // Single-part dotted base — alias.col OR schema.table OR cte.col OR
  // synthetic relation (table-variable / temp-table / OPENJSON alias) OR
  // pseudo-table (`inserted` / `deleted` from MERGE / OUTPUT).
  if (parts.length === 1) {
    // Synthetic relations match by name (`@t`, `#tmp`) or by alias
    // (OPENJSON `j`). OPENJSON aliases are statement-scoped — filter
    // out any whose declaration lies outside the current statement.
    const synth = findSyntheticRelationByBase(
      visibleSynthRels(synthRels, ctx.statementRange),
      baseLower,
    );
    if (synth) {
      emitSyntheticColumns(out, synth);
      return;
    }
    // `FROM inserted i LEFT JOIN deleted d` — the pseudo-tables are usually
    // ALIASED in a trigger body, and an alias resolves to a relation named
    // `inserted` / `deleted` that no snapshot contains, so the alias branch
    // below found no columns and the position emitted nothing at all. Resolve
    // the alias back to the pseudo-table it names and take the same path the
    // bare `inserted.` / `deleted.` base takes.
    const pseudoBase = resolvePseudoTableBase(baseLower, ctx.aliasBindings);
    // An ALIASED pseudo-table can only come from a trigger body's `FROM
    // inserted i` / `JOIN deleted d`, and there the columns are the trigger's
    // host table — never the target of an INSERT the body happens to wrap. A
    // BARE `inserted.` keeps the existing precedence, because in an OUTPUT
    // clause it names that statement's target.
    const pseudoIsAliased = pseudoBase !== null && baseLower !== pseudoBase;
    if (pseudoBase && pseudoIsAliased && triggerHostTable) {
      await emitTriggerHostColumns(out, triggerHostTable, snapshotTables, pseudoBase, input, args.defaultSchemas);
      return;
    }

    // Pseudo-tables `inserted` / `deleted` from MERGE / OUTPUT — surface
    // the columns of the DML target.
    if (pseudoBase && dmlTarget) {
      await emitDmlTargetColumns(out, dmlTarget, snapshotTables, pseudoBase, input, args.defaultSchemas);
      return;
    }
    // Trigger pseudo-tables — when the cursor is inside a CREATE TRIGGER
    // body and there is no enclosing DML OUTPUT target, `inserted` /
    // `deleted` resolve to the trigger's host table.
    if (pseudoBase && triggerHostTable) {
      await emitTriggerHostColumns(out, triggerHostTable, snapshotTables, pseudoBase, input, args.defaultSchemas);
      return;
    }
    const aliasMatch = pickAliasBinding(ctx.aliasBindings, baseLower, snapshotTables);
    if (aliasMatch) {
      await emitAliasColumns(out, aliasMatch, snapshotTables, cteBindings, input, synthRels, args.defaultSchemas);
      // F5.2-CB5 + S3 — FK/partner-aware column ranking at JOIN ON / WHERE
      // dotted positions (partner affinity → endpoint-pair FKs → name∩+PK).
      applyJoinOnColumnRanking({
        out,
        text: args.text,
        offset: ctx.offset,
        statementStart: ctx.statementRange.start,
        sqlEngine: 'mssql',
        bindings: ctx.aliasBindings,
        edges: args.snapshotForeignKeys,
        binding: aliasMatch,
        tables: snapshotTables,
      });
      // Workstream C parity (mirrors oracle 18.§1) — `JSON_VALUE(o.▮` /
      // JSON_QUERY first arg lifts the JSON document column: buffer-affinity ∪
      // bus-classified ∪ MetaInfo-declared JSON columns of the alias's table.
      if (isAtJsonFunctionFirstArg(args.text, ctx.offset, ctx.statementRange.start)) {
        applyJsonFunctionArgPromotion(out, collectMssqlKnownJsonColumns(args, aliasMatch));
      }
      return;
    }
    const cteMatch = ctx.cteBindings.find((c) => c.name.toLowerCase() === baseLower);
    if (cteMatch) {
      emitCteColumnList(out, cteMatch, {
        source: input.model.getValue(),
        engineId: 'mssql',
        snapshotTables,
        cteBindings,
      });
      // S3 partner affinity for CTE projections (`ON child.manager_id = ec.▮`
      // wants the FK pair `employee_id`) — name-based, so no FK metadata on
      // the CTE itself is needed.
      applyJoinOnColumnRanking({
        out,
        text: args.text,
        offset: ctx.offset,
        statementStart: ctx.statementRange.start,
        sqlEngine: 'mssql',
        bindings: ctx.aliasBindings,
        edges: args.snapshotForeignKeys,
      });
      return;
    }
    // System catalogs — `sys.|` and `INFORMATION_SCHEMA.|`. Static dictionary
    // (schema-bus snapshot doesn't carry catalogs). Closes mssql script.13
    // §2-§4 and §6, and script.26 fallback when the catalog isn't snapshot-
    // present. Combines with `emitTablesInSchema` so a user table that
    // happens to live in `sys` (rare but legal) still surfaces.
    if (isSysSchema(baseLower)) {
      emitSysCatalogTables(out);
      emitTablesInSchema(out, snapshotTables, baseLower);
      return;
    }
    if (isInformationSchema(baseLower)) {
      emitInformationSchemaViews(out);
      emitTablesInSchema(out, snapshotTables, baseLower);
      return;
    }
    // System-database dotted access — `master.|` / `tempdb.|` / `model.|` /
    // `msdb.|` emit the built-in per-DB schemas (`sys`, `dbo`,
    // `INFORMATION_SCHEMA`, `guest`). Closes mssql script.26.§3. Falls
    // through to `emitTablesInSchema` for user tables that happen to be
    // schema-qualified by the system DB name (unusual but legal).
    if (MSSQL_SYSTEM_DATABASE_SET.has(baseLower)) {
      emitPerDbBuiltinSchemas(out);
      emitTablesInSchema(out, snapshotTables, baseLower);
      return;
    }
    // schema.<name> — context decides what lives under the schema
    // (Workstream C, script.37 §2/§8/§9, script.42 §4/§7):
    //   * `NEXT VALUE FOR dbo.▮` — sequences ONLY.
    //   * `EXEC dbo.▮` — procedures ONLY (tables aren't EXECutable).
    //   * `FROM dbo.▮` — tables + table-valued functions.
    //   * expression positions — tables + scalar UDFs.
    if (isAtNextValueForSlot(args.text, ctx.offset, ctx.statementRange.start)) {
      emitSequencesInSchema(out, args.snapshotSequences, args.text, baseLower);
      return;
    }
    if (isAtExecTargetSlot(args.text, ctx.offset, ctx.statementRange.start)) {
      // Procedures created EARLIER IN THIS BUFFER lead: a script that writes a
      // procedure and then EXECs it is the normal flow, and the snapshot
      // cannot know that procedure until the DDL has run (script.43 §11).
      emitInBufferRoutinesInSchema(out, args.text, ctx.offset, baseLower);
      emitRoutinesInSchema(out, args.snapshotRoutines, baseLower, 'procedure');
      return;
    }
    // `@param dbo.▮` / `DECLARE @v dbo.▮` — a TYPE position under a schema.
    // The only legal completion is a user-defined type, and a TVP type the
    // buffer itself declares (`CREATE TYPE dbo.x AS TABLE (…)`) is invisible
    // to the snapshot until the DDL runs (script.43 §4/§10). Non-returning:
    // the schema's tables still emit below, since the snapshot may also carry
    // committed types this scan cannot see.
    if (isMssqlTableTypeSlot(args.text, ctx.offset, ctx.statementRange.start)) {
      emitInBufferTableTypesInSchema(out, args.text, ctx.offset, baseLower);
    }
    const clause = computeClauseContext(
      args.text,
      ctx.statementRange.start,
      ctx.offset,
      'mssql',
    )?.clause;
    if (clause === 'from') {
      emitRoutinesInSchema(out, args.snapshotRoutines, baseLower, 'table-function');
    } else if (clause !== null && clause !== undefined && clause !== 'other') {
      // Expression clauses (select / where / having / order-by / …) take
      // scalar UDF calls. A null/'other' clause is a DDL-ish statement
      // (TRUNCATE TABLE dbo.▮, DROP …) where a function is never legal —
      // emit tables only.
      emitRoutinesInSchema(out, args.snapshotRoutines, baseLower, 'scalar-function');
    }
    emitTablesInSchema(out, snapshotTables, baseLower, args.bufferFreshTableKeys);
    // Workstream C parity (mirrors pgsql) — `FOREIGN KEY (category_id)
    // REFERENCES dbo.▮`: the FK source-column stems name the likely target
    // table; lift it above the alphabetical schema listing. No-op outside a
    // REFERENCES tail, so it's inert for FROM/EXEC/expression dotted access.
    applyReferencesTargetPromotion(
      out,
      findFkSourceColumnStems(args.text, ctx.offset, ctx.statementRange.start),
    );
    return;
  }

  // Two-part dotted base — `db.schema.` or `schema.table.`. We optimistically
  // try `db.schema.` first because that's the common MSSQL pattern.
  if (parts.length === 2) {
    const part0Lower = unquoteIdentifier(parts[0]!).toLowerCase();
    const part1Lower = unquoteIdentifier(parts[1]!).toLowerCase();
    // db.schema.<table> — match snapshot tables on (database, schema).
    for (const t of snapshotTables) {
      const dbLower = (t.database ?? '').toLowerCase();
      const schemaLower = (t.schema ?? '').toLowerCase();
      if (dbLower === part0Lower && schemaLower === part1Lower) {
        out.push(makeTableCandidate(t));
      }
    }
    if (out.length > 0) return;
    // schema.table.<col> — match snapshot tables on (schema, name).
    const tbl = snapshotTables.find(
      (t) =>
        (t.schema ?? '').toLowerCase() === part0Lower &&
        t.name.toLowerCase() === part1Lower,
    );
    if (tbl && input.connectionId && input.database) {
      const cols = await ensureColumnsForTable(input.connectionId, input.database, tbl.schema ?? 'dbo', tbl.name);
      for (const c of cols) {
        out.push({
          label: c.name,
          insertText: c.name,
          isSnippet: false,
          kind: 'column',
          tier: 1,
          detail: `${c.dataType} (${tbl.schema ?? ''}.${tbl.name})`,
        });
      }
      return;
    }
    // System-catalog fallback for two-part bases:
    //   `<systemDb>.sys.|` / `<systemDb>.INFORMATION_SCHEMA.|` — catalog tables.
    //   `<schema>.<sysCatalogTable>.|` — catalog table column list (rare).
    if (MSSQL_SYSTEM_DATABASE_SET.has(part0Lower)) {
      if (isSysSchema(part1Lower)) {
        emitSysCatalogTables(out);
        return;
      }
      if (isInformationSchema(part1Lower)) {
        emitInformationSchemaViews(out);
        return;
      }
    }
    const sysCols = getSystemCatalogColumns(part0Lower, part1Lower);
    if (sysCols) {
      emitSystemCatalogColumns(out, sysCols, `${part0Lower}.${part1Lower}`);
    }
    return;
  }

  // Three-part dotted base — `linkedServer.db.schema.` or `db.schema.table.`.
  if (parts.length === 3) {
    const part0Lower = unquoteIdentifier(parts[0]!).toLowerCase();
    const part1Lower = unquoteIdentifier(parts[1]!).toLowerCase();
    const part2Lower = unquoteIdentifier(parts[2]!).toLowerCase();
    const tbl = snapshotTables.find(
      (t) =>
        (t.database ?? '').toLowerCase() === part0Lower &&
        (t.schema ?? '').toLowerCase() === part1Lower &&
        t.name.toLowerCase() === part2Lower,
    );
    if (tbl && input.connectionId && input.database) {
      const cols = await ensureColumnsForTable(input.connectionId, input.database, tbl.schema ?? 'dbo', tbl.name);
      for (const c of cols) {
        out.push({
          label: c.name,
          insertText: c.name,
          isSnippet: false,
          kind: 'column',
          tier: 1,
          detail: `${c.dataType} (${tbl.schema ?? ''}.${tbl.name})`,
        });
      }
      return;
    }
    // System-catalog three-part fallback: `<systemDb>.<sysOrInfoSchema>.<table>.|`
    // Resolve columns from the static dictionary. Closes spec patterns where
    // the user dots into a fully-qualified system catalog without an alias.
    if (MSSQL_SYSTEM_DATABASE_SET.has(part0Lower)) {
      const sysCols = getSystemCatalogColumns(part1Lower, part2Lower);
      if (sysCols) {
        emitSystemCatalogColumns(out, sysCols, `${part1Lower}.${part2Lower}`);
      }
    }
  }
}

function emitTablesInSchema(
  out: CompletionCandidate[],
  tables: BuildArgs['snapshotTables'],
  schemaLower: string,
  // Objects the BUFFER created / renamed above the caret (see
  // `computeBufferFreshTableKeys`). They are what the user just wrote, so they
  // lead the schema's alphabetical listing (script.38 §5 — the just-renamed
  // `acact_ScratchRenamed` at the `DROP TABLE … dbo.▮` caret).
  bufferFreshKeys: ReadonlySet<string> = new Set<string>(),
): void {
  for (const t of tables) {
    if ((t.schema ?? '').toLowerCase() !== schemaLower) continue;
    const fresh = bufferFreshKeys.has(`${schemaLower}.${t.name.toLowerCase()}`);
    out.push({
      label: formatIdentifier(t.name),
      insertText: formatIdentifier(t.name),
      isSnippet: false,
      kind: t.kind === 'view' ? 'view' : 'table',
      tier: fresh ? 1 : 2,
      detail: t.kind === 'view' ? 'View' : 'Table',
      filterText: t.name,
    });
  }
}

/** `@param dbo.▮` / `DECLARE @v dbo.▮` — the caret is one dot past a schema
 *  name in a TYPE position (a routine parameter's or a local's declared type).
 *  Anchored at the caret, so it only fires while the type itself is being
 *  written; `SET @x = dbo.▮` (an expression) does not match. */
function isMssqlTableTypeSlot(
  text: string,
  cursorOffset: number,
  statementStart: number,
): boolean {
  return /@[\w$]+\s+(?:\[[^\]]*\]|"(?:[^"]|"")*"|[A-Za-z_][\w$]*)\s*\.\s*[\w$]*$/.test(
    text.slice(statementStart, cursorOffset),
  );
}

/** In-buffer `CREATE TYPE <schemaLower>.<name> AS TABLE (…)` types, tier 1. */
function emitInBufferTableTypesInSchema(
  out: CompletionCandidate[],
  text: string,
  cursorOffset: number,
  schemaLower: string,
): void {
  for (const t of scanMssqlBufferTableTypes(text, cursorOffset)) {
    if ((t.schema ?? 'dbo').toLowerCase() !== schemaLower) continue;
    out.push({
      label: formatIdentifier(t.name),
      insertText: formatIdentifier(t.name),
      isSnippet: false,
      // `keyword` for the same reason `emitMssqlDataTypeCandidates` uses it —
      // the completion taxonomy has no data-type kind, and a TVP type IS a
      // type name at this caret, not a relation.
      kind: 'keyword',
      tier: 1,
      detail: `Table type (${t.columns.length} columns, this script)`,
      filterText: t.name,
    });
  }
}

/** In-buffer `CREATE [OR ALTER] PROCEDURE <schemaLower>.<name>`, tier 1. */
function emitInBufferRoutinesInSchema(
  out: CompletionCandidate[],
  text: string,
  cursorOffset: number,
  schemaLower: string,
): void {
  for (const r of scanBufferCreatedRoutines(text, cursorOffset)) {
    if (r.kind !== 'procedure') continue;
    if ((r.schema ?? 'dbo').toLowerCase() !== schemaLower) continue;
    out.push({
      label: formatIdentifier(r.name),
      insertText: formatIdentifier(r.name),
      isSnippet: false,
      kind: 'routine',
      tier: 1,
      detail: 'Stored procedure (this script)',
      filterText: r.name,
    });
  }
}

// Curated everyday system stored procedures for the EXEC-target slot.
// Deliberately short — the full sp_* surface is enormous; these are the ones
// users reach for at an interactive prompt (script.41 §2 anchors + staples).
const MSSQL_SYSTEM_PROCEDURES: readonly string[] = [
  'sp_executesql',
  'sp_help',
  'sp_helptext',
  'sp_helpdb',
  'sp_helpindex',
  'sp_rename',
  'sp_who',
  'sp_who2',
  'sp_lock',
  'sp_columns',
  'sp_tables',
  'sp_spaceused',
  'sp_configure',
  'sp_recompile',
  'sp_updatestats',
];

function emitMssqlSystemProcedures(out: CompletionCandidate[]): void {
  for (const name of MSSQL_SYSTEM_PROCEDURES) {
    out.push({
      label: name,
      insertText: name,
      isSnippet: false,
      kind: 'routine',
      // Tier 2 — below the user's own procedures, which is what someone typing
      // EXEC almost always wants. The curated `sp_*` set stays one keystroke of
      // filtering away.
      tier: 2,
      detail: 'System stored procedure',
      filterText: name,
    });
  }
}

/**
 * Emit snapshot routines living in `schemaLower`, filtered by shape:
 * `'procedure'` (EXEC targets, plain insert — T-SQL EXEC takes no parens),
 * `'table-function'` (FROM positions; `returns === 'table'`), or
 * `'scalar-function'` (expression positions). Function shapes insert a
 * `name($0)` call snippet since both require parentheses at the call site.
 */
function emitRoutinesInSchema(
  out: CompletionCandidate[],
  routines: readonly RoutineMeta[],
  schemaLower: string,
  shape: 'procedure' | 'table-function' | 'scalar-function',
): void {
  // A routine the BUFFER re-creates (`CREATE OR ALTER PROCEDURE dbo.p`) is
  // emitted by `emitInBufferRoutinesInSchema` first, at tier 1. Skip the
  // snapshot copy so the caret shows one entry, not two.
  const already = new Set(
    out.filter((c) => c.kind === 'routine').map((c) => c.label.toLowerCase()),
  );
  for (const r of routines) {
    if ((r.schema ?? '').toLowerCase() !== schemaLower) continue;
    if (already.has(r.name.toLowerCase())) continue;
    const isTvf = r.kind === 'function' && r.returns === 'table';
    const matches =
      shape === 'procedure'
        ? r.kind === 'procedure'
        : shape === 'table-function'
          ? isTvf
          : r.kind === 'function' && !isTvf;
    if (!matches) continue;
    const isProc = shape === 'procedure';
    out.push({
      label: r.name,
      insertText: isProc ? r.name : `${r.name}($0)`,
      isSnippet: !isProc,
      kind: 'routine',
      tier: isProc ? 1 : 2,
      detail: isProc
        ? 'Stored procedure'
        : isTvf
          ? 'Table-valued function'
          : 'Scalar function',
      filterText: r.name,
    });
  }
}

/**
 * Surface the matched procedure's parameter names at an EXEC argument slot
 * (script.37 §3-§5). Parameter names come from `sys.parameters` so they carry
 * the leading `@` already. No already-passed dedup: T-SQL tolerates re-typing
 * a name and the spec expects the full signature at every argument slot.
 */
function emitExecProcParameters(
  out: CompletionCandidate[],
  routines: readonly RoutineMeta[],
  slot: ExecNamedParamSlot,
): void {
  const nameLower = slot.procName.toLowerCase();
  const schemaLower = slot.procSchema?.toLowerCase() ?? null;
  const proc = routines.find(
    (r) =>
      r.kind === 'procedure'
      && r.name.toLowerCase() === nameLower
      && (schemaLower === null || (r.schema ?? '').toLowerCase() === schemaLower),
  );
  if (!proc) return;
  const procLabel = proc.schema ? `${proc.schema}.${proc.name}` : proc.name;
  for (const p of proc.parameters) {
    if (p.mode === 'return') continue;
    out.push({
      label: p.name,
      insertText: p.name,
      isSnippet: false,
      kind: 'variable',
      tier: 1,
      detail: `${p.dataType} (${p.mode === 'out' ? 'OUTPUT ' : ''}parameter of ${procLabel})`,
      filterText: p.name,
    });
  }
}

/**
 * Snapshot sequences merged with buffer-declared ones (`CREATE SEQUENCE` in
 * the editor that hasn't executed yet — the snapshot can't know it, script.42
 * completes against its own CREATE). Buffer-declared rank tier 1, snapshot
 * tier 2; ci-dedup on (schema, name).
 */
function collectSequences(
  snapshot: readonly SequenceMeta[],
  text: string,
): { name: string; schema: string | null; fromBuffer: boolean }[] {
  const seen = new Set<string>();
  const out: { name: string; schema: string | null; fromBuffer: boolean }[] = [];
  for (const b of scanBufferCreatedSequences(text)) {
    const key = `${(b.schema ?? '').toLowerCase()}|${b.name.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ name: b.name, schema: b.schema, fromBuffer: true });
  }
  for (const s of snapshot) {
    const key = `${(s.schema ?? '').toLowerCase()}|${s.name.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ name: s.name, schema: s.schema ?? null, fromBuffer: false });
  }
  return out;
}

/** Schema-qualified sequence candidates at a bare `NEXT VALUE FOR ▮` slot. */
function emitQualifiedSequences(
  out: CompletionCandidate[],
  sequences: readonly SequenceMeta[],
  text: string,
): void {
  for (const s of collectSequences(sequences, text)) {
    const label = s.schema ? `${s.schema}.${s.name}` : s.name;
    out.push({
      label,
      insertText: s.schema
        ? formatQualifiedTwoPart(s.schema, s.name)
        : formatIdentifier(s.name),
      isSnippet: false,
      kind: 'system-object',
      tier: s.fromBuffer ? 1 : 2,
      detail: 'Sequence',
      filterText: label,
    });
  }
}

/** Bare sequence names at `NEXT VALUE FOR <schema>.▮`. */
function emitSequencesInSchema(
  out: CompletionCandidate[],
  sequences: readonly SequenceMeta[],
  text: string,
  schemaLower: string,
): void {
  for (const s of collectSequences(sequences, text)) {
    if ((s.schema ?? '').toLowerCase() !== schemaLower) continue;
    out.push({
      label: s.name,
      insertText: formatIdentifier(s.name),
      isSnippet: false,
      kind: 'system-object',
      tier: s.fromBuffer ? 1 : 2,
      detail: 'Sequence',
      filterText: s.name,
    });
  }
}

function makeTableCandidate(
  t: BuildArgs['snapshotTables'][number],
  fkLinked = false,
): CompletionCandidate {
  const schema = t.schema ?? '';
  const insertText = schema
    ? formatQualifiedTwoPart(schema, t.name)
    : formatIdentifier(t.name);
  // System catalogs (`sys.*`, `INFORMATION_SCHEMA.*`) demote to tier 8 so
  // they sit at the bottom of the suggestion list. Power users can still
  // surface them by typing the schema prefix; regular completion gets a
  // cleaner top-of-list. FK-linked tables (an FK edge connects them to a
  // bound relation) promote to tier 1 — the actually-joinable candidates
  // at a JOIN slot, above unrelated tables and their own FK snippets.
  const isSystem = isMssqlSystemCatalog(t.schema);
  const tier: CompletionCandidate['tier'] = isSystem ? 8 : fkLinked ? 1 : 2;
  const detailPrefix = isSystem ? 'System ' : '';
  return {
    label: schema ? `${schema}.${t.name}` : t.name,
    insertText,
    isSnippet: false,
    kind: t.kind === 'view' ? 'view' : 'table',
    tier,
    detail: t.kind === 'view' ? `${detailPrefix}View` : `${detailPrefix}Table`,
    filterText: t.name,
    // System-catalog tables/views share the unbounded 'table'/'view' kind
    // with thousands of real user tables — flag so the ranker's untyped
    // cap can't slice them on a >500-table schema (Plan 15 Gate D class
    // kill, register #41 symmetric fix; twin of the oracle dictionary-view
    // synonym exemption in emitSynonymsAsRelations). User tables (isSystem
    // false) stay cappable.
    ...(isSystem ? { capExempt: true } : {}),
  };
}

/**
 * The four universal MSSQL system databases. Present on every SQL Server
 * install; users sometimes need them for three-part references (e.g.
 * `master.sys.databases`, `tempdb.sys.objects`). Surfaced at tier 8 so they
 * never out-rank the user's working database.
 */
const MSSQL_SYSTEM_DATABASES: readonly string[] = ['master', 'tempdb', 'model', 'msdb'];

/**
 * Emit database candidates at FROM position. The mssql schema-bus snapshot
 * doesn't carry a `databases` list today — closing the cross-database
 * discovery gap fully requires a `sys.databases` IPC fetcher + native cache +
 * bus projection. For now we surface (a) the current database at tier 4 (the
 * common case — `currentdb.schema.table` references) and (b) the four
 * universal system databases at tier 8. Together these close the spec's
 * expectations at FROM (mssql script.26.§2, §5). User databases beyond the
 * current one remain out of scope until the IPC layer is built.
 */
/** True at `USE ▮` — the statement's only token is `USE`, optionally followed
 *  by the partial database name being typed. */
function isMssqlUseStatementTarget(
  source: string,
  statementStart: number,
  cursor: number,
): boolean {
  return /^\s*USE\s+[A-Za-z0-9_$#[\]".]*$/i.test(source.slice(statementStart, cursor));
}

function emitMssqlDatabases(
  out: CompletionCandidate[],
  currentDatabase: string | undefined,
): void {
  const seen = new Set<string>();
  if (currentDatabase) {
    seen.add(currentDatabase.toLowerCase());
    out.push({
      label: currentDatabase,
      insertText: formatIdentifier(currentDatabase),
      isSnippet: false,
      kind: 'database',
      tier: 4,
      detail: 'Database',
      filterText: currentDatabase,
    });
  }
  for (const s of MSSQL_SYSTEM_DATABASES) {
    if (seen.has(s)) continue;
    out.push({
      label: s,
      insertText: formatIdentifier(s),
      isSnippet: false,
      kind: 'database',
      tier: 8,
      detail: 'System database',
      filterText: s,
      // Fixed 4-name engine-curated set sharing the unbounded 'database'
      // kind — exempt from the ranker's untyped cap (Plan 15 Gate D round
      // 2/3 class kill; twin of MYSQL_SYSTEM_DATABASES). User databases
      // (currentDatabase above) stay cappable.
      capExempt: true,
    });
  }
}

function emitTablesAndSchemas(
  out: CompletionCandidate[],
  tables: BuildArgs['snapshotTables'],
  schemas: readonly string[],
  fkLinkedKeys?: ReadonlySet<string>,
): void {
  // Schemas at tier 4 (other-visible-schemas slot).
  for (const s of schemas) {
    out.push({
      label: formatIdentifier(s),
      insertText: formatIdentifier(s),
      isSnippet: false,
      kind: 'schema',
      tier: 4,
      detail: 'Schema',
      filterText: s,
      // The engine-reserved sys/INFORMATION_SCHEMA schemas share the
      // unbounded 'schema' kind with user schemas — flag so the ranker's
      // untyped cap can't slice them on a >500-table schema (register #41
      // symmetric fix; twin of pgsql's PG_SYSTEM_SCHEMAS exemption in
      // emitTablesAndSchemas). User schemas stay cappable.
      ...(isMssqlSystemCatalog(s) ? { capExempt: true } : {}),
    });
  }
  // Tables at tier 2 (current-database tables); FK-linked ones at tier 1.
  for (const t of tables) {
    out.push(makeTableCandidate(t, fkLinkedKeys?.has(fkLinkedTableKey(t)) ?? false));
  }
}

function emitCteCandidates(out: CompletionCandidate[], ctx: BuildArgs['ctx']): void {
  for (const cte of ctx.cteBindings) {
    out.push({
      label: cte.name,
      insertText: cte.name,
      isSnippet: false,
      kind: 'cte',
      tier: 5,
      detail: 'CTE',
      filterText: cte.name,
    });
  }
}

// CB1.7 — Continuation keywords that should rank tier 1 at the
// "select-list, after completed projection" sub-position. Intersect with
// MSSQL_KEYWORDS so we only promote what the existing emission actually
// emits — promoting a label that's never produced is a no-op.
const MSSQL_NEXT_AFTER_SELECT_PROJECTION: ReadonlySet<string> = new Set(
  ['FROM', 'INTO', 'UNION', 'EXCEPT', 'INTERSECT', 'FOR', 'OPTION'].filter((k) =>
    MSSQL_KEYWORDS.has(k),
  ),
);

// CB1.7 — Continuation keywords that should rank tier 1 at the
// "from, after completed relation" sub-position.
const MSSQL_NEXT_AFTER_FROM_RELATION: ReadonlySet<string> = new Set(
  [
    'WHERE', 'JOIN', 'INNER', 'LEFT', 'RIGHT', 'FULL', 'OUTER', 'CROSS',
    'ON', 'AS', 'ORDER', 'GROUP', 'HAVING', 'UNION', 'INTERSECT', 'EXCEPT',
    'OPTION', 'FOR', 'WITH',
  ].filter((k) => MSSQL_KEYWORDS.has(k)),
);

// MSSQL multi-token operators at WHERE/HAVING position. `IS NULL` and
// `IS NOT NULL` are two-token operators the curated single-keyword emitter
// cannot express, so the resolver pushes them directly when the position
// is `where` or `having`. MSSQL does NOT accept `NULLS FIRST` / `NULLS LAST`
// (that's PG/Oracle/SQLite syntax) — so no ORDER BY null-ordering emission.
const MSSQL_WHERE_NULL_OPERATOR_KEYWORDS: readonly string[] = [
  'IS NULL',
  'IS NOT NULL',
];

// Multi-token continuation at `limit-offset` position. T-SQL's paging clause
// requires `FETCH NEXT <n> ROWS ONLY` (`ROWS ONLY` is a fixed two-token
// terminator after the row count). Pinned by goals/autocomplete-coverage
// mssql script.20 §4. The single-keyword emitter already surfaces `ROWS`
// and `ONLY` individually; this list adds the compound that the spec asserts.
const MSSQL_LIMIT_OFFSET_MULTI_KEYWORDS: readonly string[] = [
  'ROWS ONLY',
];

// Emit ONLY the grammatically-legal continuation keywords for a recognized
// sub-position, ordered by popularity (orderHint = index). Returns true when
// it emitted (caller should then return — these are keyword-only boundaries).
function emitLegalNextKeywords(
  out: CompletionCandidate[],
  slot: NextKeywordSlotResult,
  // Shifts the popularity order when a leading candidate (the generated
  // table alias) already occupies orderHint 0.
  orderHintBase = 0,
): boolean {
  const kws = getLegalNextKeywords(ENGINE, slot.slot, {
    relationHasAlias: slot.relationHasAlias,
    projectionIsStar: slot.projectionIsStar,
  });
  if (kws.length === 0) return false;
  // The slot IS answered whenever it has a vocabulary, so `true` (the caller's
  // short-circuit) does not depend on how many items are new — a keyword an
  // earlier, more specific emission already offered (a compound remainder at
  // `NULLS F▮`) is skipped rather than repeated in the widget.
  const alreadyOffered = keywordLabelsAlreadyOffered(out);
  kws.forEach((kw, i) => {
    if (alreadyOffered.has(kw.toUpperCase())) return;
    out.push({ label: kw, insertText: kw, isSnippet: false, kind: 'keyword', tier: 1, detail: 'SQL keyword', orderHint: orderHintBase + i });
  });
  return true;
}

/**
 * Clause keywords that cannot START a select-list item. Each needs a completed
 * projection AND a FROM clause before it becomes legal, so at `SELECT ▮` they
 * are pure noise (script.42 §5 pins their absence). Single-token, matching the
 * ladder vocabulary the whole keyword layer uses (`GROUP` then `BY`).
 *
 * ENGINE SCOPE: mssql-only because the keyword dump this filters is mssql's
 * own (`getMssqlKeywordsForPosition`); the other four resolvers each have their
 * own list, and no spec row asserts the suppression for them yet.
 */
const MSSQL_ILLEGAL_AT_SELECT_ITEM_HEAD: ReadonlySet<string> = new Set([
  'WHERE', 'GROUP', 'HAVING', 'ORDER',
]);

function emitKeywords(
  out: CompletionCandidate[],
  nextLikely: ReadonlySet<string> = new Set(),
  promotedOrderHint?: number,
  suppressed?: ReadonlySet<string>,
): void {
  const keywords = getMssqlKeywordsForPosition();
  const alreadyOffered = keywordLabelsAlreadyOffered(out);
  for (const kw of keywords) {
    if (suppressed?.has(kw)) continue;
    // A more specific emission already offered this keyword (e.g. a compound
    // remainder at tier 1) — don't repeat it in the widget.
    if (alreadyOffered.has(kw)) continue;
    const promoted = nextLikely.has(kw);
    out.push({
      label: kw,
      insertText: kw,
      isSnippet: false,
      kind: 'keyword',
      tier: promoted ? 1 : 7,
      detail: 'SQL keyword',
      ...(promoted && promotedOrderHint !== undefined ? { orderHint: promotedOrderHint } : {}),
    });
  }
}

// Statement-starter keywords promoted to the top of the suggest list at a
// procedural body statement-start (see the fallback in `buildCandidates`).
const MSSQL_PROC_STATEMENT_STARTERS: ReadonlySet<string> = new Set([
  'SELECT', 'INSERT', 'UPDATE', 'DELETE', 'MERGE', 'DECLARE', 'SET', 'IF',
  'WHILE', 'BEGIN', 'EXEC', 'EXECUTE', 'RETURN', 'THROW', 'PRINT', 'RAISERROR',
  'WAITFOR', 'GOTO', 'BREAK', 'CONTINUE', 'COMMIT', 'ROLLBACK', 'SAVE', 'OPEN',
  'CLOSE', 'FETCH', 'DEALLOCATE', 'TRUNCATE', 'WITH',
]);

/**
 * Emit T-SQL built-in function names from the curated list. Tier 8
 * (system-function) so built-ins sit at the bottom of the suggestion list —
 * power users still find them via fuzzy match. Mirrors the mysql / pgsql
 * equivalents (`emitBuiltinFunctions` in those resolvers). MSSQL's
 * `SchemaSnapshot.routines` is currently always empty (signature help still
 * owned by SSTS), so no dedupe needed; if user routines ever flow through the
 * bus later this should grow a userNames Set just like the mysql variant.
 *
 * `keywordsAlreadyEmitted` (default true) governs the dedup below — see its
 * doc at the call site guard. Pass `false` from a branch that never calls
 * `emitKeywords` (predicate-head plan P8a) so the keyword/builtin-overlap
 * names aren't silently dropped.
 */
function emitMssqlBuiltinFunctions(
  out: CompletionCandidate[],
  keywordsAlreadyEmitted = true,
): void {
  for (const fn of getMssqlBuiltinFunctions()) {
    // Dedup against the curated keyword list: T-SQL keywords like CAST,
    // CONVERT, TRY_CAST, TRY_CONVERT, COALESCE, NULLIF, MAX, SUM, PARSE,
    // ISNULL surface via the keyword path at tier 7 already (when this
    // branch's caller ran `emitKeywords` first). Emitting them a second time
    // as tier-8 snippet builtins produces two model entries with the same
    // label, and Monaco's `_completionModel.items` retains only one — the
    // snippet entry tends to win and then gets filtered out by the
    // surrounding completion context, so the label disappears from the
    // visible list. Skipping keywords here means only the keyword emission
    // flows through, which is what every other engine does too.
    //
    // Predicate-head plan P8a — the predicate-head branch never calls
    // `emitKeywords` (PH-D policy owns its own narrow keyword/snippet set,
    // not the full clause-start keyword flood), so for that caller the
    // dedup assumption is false: skipping keyword-overlap names there drops
    // them from the suggestion list ENTIRELY (script.16 §4's TRY_CAST/
    // TRY_PARSE/PARSE, script.51 §1's SUM/MAX) — contradicting PH-D4's
    // "builtins stay engine-emitted, exactly as the scalar-value branch
    // ships them" intent. `keywordsAlreadyEmitted = false` keeps the overlap
    // names in that case. (The pre-existing scalar-value/comparison-rhs
    // branch has the SAME latent gap since T13b — out of scope for this
    // flip; not touched here.)
    if (keywordsAlreadyEmitted && MSSQL_KEYWORDS.has(fn)) continue;
    out.push({
      label: fn,
      insertText: `${fn}($0)`,
      isSnippet: true,
      kind: 'system-function',
      tier: 8,
      detail: 'T-SQL function',
      filterText: fn,
      triggerSignatureHelp: true,
    });
  }
}

/**
 * Emit GROUP BY extension keywords (`GROUPING SETS`, `ROLLUP`, `CUBE`).
 * Called only at `group-by` position because these are syntactic extensions
 * to the GROUP BY clause, not generic keywords. Tier 7 (keyword tier) — they
 * compete with column references in the GROUP BY slot and the ranker keeps
 * columns dominant.
 */
function emitMssqlGroupByExtensionKeywords(out: CompletionCandidate[]): void {
  for (const kw of getMssqlGroupByExtensionKeywords()) {
    out.push({
      label: kw,
      insertText: kw,
      isSnippet: false,
      kind: 'keyword',
      tier: 7,
      detail: 'T-SQL GROUP BY extension',
    });
  }
}

/**
 * F5.3 Wave-4 — emit the curated T-SQL data type list at a "data-type" slot
 * (CREATE TABLE column-type, DECLARE @x type, CREATE PROC/FUNCTION @param
 * type, CAST/TRY_CAST/PARSE/CONVERT target). Tier 1 (dominant) so the type
 * vocabulary tops the suggestion list at the type slot. Kind is `keyword`
 * because Monaco renders SQL data types as keyword-shaped tokens (no
 * dedicated `data-type` kind exists in the completion taxonomy).
 */
function emitMssqlDataTypeCandidates(out: CompletionCandidate[]): void {
  for (const type of getMssqlDataTypes()) {
    out.push({
      label: type,
      insertText: type,
      isSnippet: false,
      kind: 'keyword',
      tier: 1,
      detail: 'T-SQL data type',
    });
  }
}

/**
 * True when the caret's data-type slot is a ROUTINE PARAMETER's type — the one
 * type position where `OUTPUT` may follow. Requires an unclosed `(` opened by
 * a `CREATE|ALTER [OR ALTER] PROCEDURE|PROC|FUNCTION <name> (` header, or the
 * parenless procedure-header form T-SQL also accepts. A CREATE TABLE column
 * type and a CAST target both answer false.
 */
function isMssqlRoutineParamTypeSlot(
  text: string,
  cursorOffset: number,
  statementStart: number,
): boolean {
  const slice = text.slice(statementStart, cursorOffset);
  if (!/\b(?:CREATE|ALTER)\s+(?:OR\s+ALTER\s+)?(?:PROC(?:EDURE)?|FUNCTION)\b/i.test(slice)) {
    return false;
  }
  // The caret must sit after a `@param` — the parameter name the type follows.
  // `[^)]*` keeps the match inside the current parameter's own segment.
  return /@[\w$]+\s+[^,()]*$/.test(slice) || /@[\w$]+\s+[A-Za-z_][\w$]*\s*\([^)]*\)\s*[^,()]*$/.test(slice);
}

function emitMssqlDatepartCandidates(out: CompletionCandidate[]): void {
  // The vocabulary is curated in display order (full name, then that part's
  // abbreviations), which only survives to the list if it is carried as an
  // `orderHint` — otherwise the ranker falls back to alphabetical and the
  // abbreviations (`d`, `dd`, `dw`, …) bury `year` / `month` / `day`.
  getMssqlDateparts().forEach((part, index) => {
    out.push({
      label: part,
      insertText: part,
      isSnippet: false,
      kind: 'keyword',
      tier: 1,
      detail: 'T-SQL datepart',
      orderHint: index,
    });
  });
}

function emitMssqlConvertStyleCandidates(out: CompletionCandidate[]): void {
  // Same `orderHint` reason as the dateparts above: the vocabulary's value is
  // its curated order (`121` / `126` first), and an alphabetical fallback on
  // NUMERIC labels is worse still — it sorts `1`, `100`, `101`, `11`, `112`.
  getMssqlConvertStyles().forEach((style, index) => {
    out.push({
      label: style.code,
      insertText: style.code,
      isSnippet: false,
      kind: 'keyword',
      tier: 1,
      detail: `CONVERT style — ${style.meaning}`,
      orderHint: index,
    });
  });
}

// GRA-8 S2 — window-frame extent keyword vocabulary. The frame-start slot
// (right after `ROWS|RANGE BETWEEN`) accepts a lower bound (`… PRECEDING` /
// `CURRENT ROW`); the frame-end slot (after the separating `AND`) accepts an
// upper bound (`… FOLLOWING` / `CURRENT ROW`). `N PRECEDING` / `N FOLLOWING`
// stand in for the numeric-offset form (the spec's must-include token). Pinned
// by goals/autocomplete-coverage mssql script.52 §5/§6/§9.
const MSSQL_WINDOW_FRAME_START_KEYWORDS: readonly string[] = [
  'UNBOUNDED PRECEDING',
  'N PRECEDING',
  'CURRENT ROW',
];
const MSSQL_WINDOW_FRAME_END_KEYWORDS: readonly string[] = [
  'UNBOUNDED FOLLOWING',
  'N FOLLOWING',
  'CURRENT ROW',
];

/**
 * Emit the directional window-frame extent vocabulary at a `ROWS|RANGE BETWEEN`
 * slot. Tier 1 (dominant) so the frame keywords top the suggestion list — the
 * caller short-circuits, so column / aggregate candidates never reach this
 * slot. Kind is `keyword` (frame extents are keyword-shaped tokens).
 */
function emitMssqlWindowFrameKeywords(
  out: CompletionCandidate[],
  slot: MssqlWindowFrameSlot,
): void {
  const keywords =
    slot === 'frame-start'
      ? MSSQL_WINDOW_FRAME_START_KEYWORDS
      : MSSQL_WINDOW_FRAME_END_KEYWORDS;
  for (const kw of keywords) {
    out.push({
      label: kw,
      insertText: kw,
      isSnippet: false,
      kind: 'keyword',
      tier: 1,
      detail: 'T-SQL window frame',
    });
  }
}

/**
 * JCS Slice C3 — type-aware JSON operator emission for MSSQL. Walks back
 * from the cursor to find the preceding column operand; if it resolves to
 * a JSON-classified column (`check-isjson` via `CHECK (ISJSON(col) = 1)`,
 * or `loose-json-declared` once MetaInfo flows through the bus), emits the
 * dialect-correct JSON operator list (`JSON_VALUE`, `JSON_QUERY`,
 * `JSON_PATH_EXISTS`, `ISJSON`, `EXISTS OPENJSON`) via SR-RO's
 * `suggestOperatorsForOperand`. Non-JSON columns yield no JSON operator
 * candidates.
 */
function emitTypeAwareJsonOperators(out: CompletionCandidate[], args: BuildArgs): void {
  const operand = findPrecedingColumnOperand({
    text: args.text,
    offset: args.ctx.offset,
    statementStart: args.ctx.statementRange.start,
    aliasBindings: args.ctx.aliasBindings,
    snapshotTables: args.snapshotTables,
    fold: (s) => unquoteIdentifier(s).toLowerCase(),
  });
  if (operand) {
    emitTypeAwareOperators(out, operand.jsonClassification, operand.column.dataType, 'mssql', args.ctx.positionKind);
    return;
  }
  // Slice 3a — typed expression operand (`CAST(x AS INT) ▮`, `LEN(c) ▮`).
  const exprType = findPrecedingExpressionType({
    text: args.text,
    offset: args.ctx.offset,
    statementStart: args.ctx.statementRange.start,
    engine: 'mssql',
    resolveRoutineReturnType: makeRoutineReturnResolver(args.snapshotRoutines, (s) => s.toLowerCase()),
  });
  if (exprType) emitTypeAwareExpressionOperators(out, exprType, 'mssql', args.ctx.positionKind);
}

type MssqlSnapshotTable = {
  readonly name: string;
  readonly schema?: string;
  readonly database?: string;
  readonly kind: string;
  readonly columns: ReadonlyArray<{
    readonly name: string;
    readonly dataType: string;
    readonly jsonClassification?: import('@/features/sql-resolver/types/schema').JsonClassification;
  }>;
};

/**
 * JCS Slice C4 — JSON path completion for MSSQL. Mirrors PG's
 * `collectPgJsonPathCandidates`. Detects whether the cursor sits inside a
 * recognised JSON-path string (after `JSON_VALUE(col, `, `JSON_QUERY(col, `,
 * `JSON_MODIFY(col, `, `JSON_PATH_EXISTS(col, `), resolves the LHS column
 * reference against the snapshot honouring FROM/JOIN aliases via the shared
 * resolveColumnRefToTable (bindings come from ctx.aliasBindings, a
 * token-stream scan independent of the tree-sitter tree), using MSSQL's
 * bracket/quote-aware case-insensitive fold, and emits sampled path hints
 * from the per-session cache when the column is JSON-classified.
 */
function collectMssqlJsonPathCandidates(args: {
  text: string;
  offset: number;
  connectionId: string;
  database: string;
  snapshotTables: ReadonlyArray<MssqlSnapshotTable>;
  readonly aliasBindings: readonly AliasBinding[];
  /** The table a relation reference names, as SQL Server resolves it (default schema, then `dbo`). */
  readonly resolveTable: RelationResolver<MssqlSnapshotTable>;
}): CompletionCandidate[] {
  const pos = findJsonPathStringPosition(args.text, args.offset);
  if (!pos) return [];
  const colSegment = readMssqlIdentifierBefore(args.text, pos.columnSideEnd);
  if (!colSegment) return [];
  const columnRef = colSegment.parts.join('.');
  const resolved = resolveColumnRefToTable(
    columnRef,
    args.aliasBindings,
    args.snapshotTables,
    (s) => unquoteIdentifier(s).toLowerCase(),
    args.resolveTable,
  );
  if (!resolved) return [];
  const tbl = resolved.table;
  const colMeta = resolved.column;
  const hintSchema = tbl.schema ?? '';
  // Offer JSON-path hints when the bus classified the column JSON
  // (engine-native / CHECK) OR the user declared it via MetaInfo
  // ("Treat as JSON" — a plain string column the schema bus can't know
  // about). The declaration source is invalidated by the Table Designer
  // toggle, so this reflects changes without a reconnect.
  const busJson =
    !!colMeta.jsonClassification &&
    colMeta.jsonClassification.kind !== 'not-json';
  if (
    !busJson &&
    !isJsonColumnDeclared(
      args.connectionId, args.database, hintSchema, tbl.name, colMeta.name,
    )
  ) {
    return [];
  }
  const cacheEntry = getPathHints(args.connectionId, args.database, {
    engine: 'mssql',
    schema: hintSchema,
    table: tbl.name,
    column: colMeta.name,
  });
  const declaredHints = getJsonPathHints(
    args.connectionId,
    args.database,
    hintSchema,
    tbl.name,
    colMeta.name,
  );
  const out: CompletionCandidate[] = [];
  emitJsonPathCandidates(out, {
    cacheEntry,
    declaredHints,
    positionKind: pos.kind,
    partial: pos.partial,
    engine: 'mssql',
  });
  return out;
}

/**
 * Phase 3 — enum-value completion for MSSQL. Resolves the LHS column
 * reference against the snapshot honouring FROM/JOIN aliases via the shared
 * resolveColumnRefToTable (bindings come from ctx.aliasBindings, a
 * token-stream scan independent of the tree-sitter tree), keyed off a
 * comparison value position rather than a JSON-path string.
 *
 * Path-aware extension (MERGE-2): when the LHS of the comparison is a
 * `JSON_VALUE(col, '$…')` / `JSON_QUERY(col, '$…')` expression, the
 * resolved canonical path is passed to `emitEnumValueCandidates` so the
 * path-keyed MetaInfo enum cache is consulted instead of the column-level
 * cache. Wildcard probe is handled inside `emitEnumValueCandidates`.
 */
function collectMssqlEnumValueCandidates(args: {
  text: string;
  offset: number;
  connectionId: string;
  database: string;
  snapshotTables: ReadonlyArray<MssqlSnapshotTable>;
  readonly aliasBindings: readonly AliasBinding[];
  /** The table a relation reference names, as SQL Server resolves it (default schema, then `dbo`). */
  readonly resolveTable: RelationResolver<MssqlSnapshotTable>;
}): CompletionCandidate[] {
  // Table DDL carries no queried relation, so a bare column reference here
  // must not fall back to an unrelated snapshot table's cached values
  // (see `isInsideTableDdlStatement`). Closes oracle script.29 §4.
  if (isInsideTableDdlStatement(args.text, args.offset)) return [];
  const cmp = detectComparisonValueContext(args.text, args.offset);
  if (!cmp) {
    // detectComparisonValueContext misses JSON-chain LHS (JSON_VALUE(col, '…') = …)
    // because the char immediately before `=` is `)` (the closing paren of the
    // call), not part of an identifier. Try the JSON-path extractor directly
    // against each candidate column; the extractor's own
    // findComparisonOpEndBefore gate keeps this cheap.
    return collectMssqlJsonChainEnumValueCandidates(args);
  }
  const resolved = resolveColumnRefToTable(
    cmp.columnRef,
    args.aliasBindings,
    args.snapshotTables,
    (s) => unquoteIdentifier(s).toLowerCase(),
    args.resolveTable,
  );
  if (!resolved) return [];
  const tbl = resolved.table;
  const colMeta = resolved.column;

  // Try to extract a JSON path from the cursor context (MSSQL-specific).
  // `extractJsonPathFromCursorMssql` looks at the LHS of the comparison
  // operator for JSON_VALUE / JSON_QUERY calls and returns the canonical path.
  const pathResult = extractJsonPathFromCursorMssql({
    text: args.text,
    cursorOffset: args.offset,
    column: colMeta.name,
  });

  const out: CompletionCandidate[] = [];
  emitEnumValueCandidates(out, {
    connectionId: args.connectionId,
    database: args.database,
    schema: tbl.schema ?? '',
    table: tbl.name,
    column: colMeta.name,
    // Pass the canonical path when a JSON-extract expression was detected.
    path: pathResult?.canonicalPath,
    quote: findValueStringBodyStart(args.text, args.offset) === undefined,
  });
  return out;
}

/**
 * Fallback for JSON-chain LHS comparisons that `detectComparisonValueContext`
 * misses (e.g. `JSON_VALUE(col, '$.key') = '|'`). Iterates over every column
 * visible via the alias bindings and snapshot tables, runs the MSSQL
 * JSON-path extractor for each, and emits enum-value candidates for the first
 * match.
 *
 * The extractor's own `findComparisonOpEndBefore` gate makes this cheap: it
 * returns `null` immediately when the cursor is not at a comparison RHS, so
 * the loop exits fast in non-matching positions.
 */
function collectMssqlJsonChainEnumValueCandidates(args: {
  text: string;
  offset: number;
  connectionId: string;
  database: string;
  snapshotTables: ReadonlyArray<MssqlSnapshotTable>;
  readonly aliasBindings: readonly AliasBinding[];
  /** The table a relation reference names, as SQL Server resolves it (default schema, then `dbo`). */
  readonly resolveTable: RelationResolver<MssqlSnapshotTable>;
}): CompletionCandidate[] {
  const quote = findValueStringBodyStart(args.text, args.offset) === undefined;

  // The extractor's backward scan never depends on the column (it is only
  // compared at the end) — scan once, then match the column it names. This
  // fallback runs on nearly every request; one scan per snapshot column was
  // ~20% of provider time on a 600-table schema (1.5.1 F2).
  const pathResult = extractJsonPathFromCursorMssql({ text: args.text, cursorOffset: args.offset });
  if (!pathResult) return [];
  const targetColumn = pathResult.column.toLowerCase();
  // The statement's own relations (as SQL Server resolves them) first, then
  // the first snapshot table owning the column.
  for (const tbl of boundTablesFirst(args.aliasBindings, args.snapshotTables, args.resolveTable)) {
    for (const col of tbl.columns) {
      if (col.name.toLowerCase() !== targetColumn) continue;

      const out: CompletionCandidate[] = [];
      emitEnumValueCandidates(out, {
        connectionId: args.connectionId,
        database: args.database,
        schema: tbl.schema ?? '',
        table: tbl.name,
        column: col.name,
        path: pathResult.canonicalPath,
        quote,
      });
      if (out.length > 0) return out;
    }
  }
  return [];
}

/**
 * Read a 1–3-part dotted identifier ending at `offset`. Accepts bare,
 * bracketed `[name]`, and double-quoted `"name"` segments — MSSQL's three
 * input forms.
 */
function readMssqlIdentifierBefore(
  text: string,
  offset: number,
): { readonly parts: readonly string[] } | null {
  let i = offset;
  while (i > 0 && /\s/.test(text[i - 1]!)) i -= 1;
  const parts: string[] = [];
  while (i > 0) {
    const end = i;
    while (i > 0 && /[A-Za-z0-9_"$[\]]/.test(text[i - 1]!)) i -= 1;
    if (i === end) break;
    parts.unshift(text.slice(i, end));
    while (i > 0 && /\s/.test(text[i - 1]!)) i -= 1;
    if (text[i - 1] !== '.') break;
    i -= 1;
    while (i > 0 && /\s/.test(text[i - 1]!)) i -= 1;
  }
  return parts.length > 0 ? { parts } : null;
}

/**
 * Emit FK-driven JOIN snippet candidates. For each candidate table in the
 * snapshot that has at least one FK edge to/from a bound alias, generate
 * one snippet per applicable edge. The ranker promotes `fkJoin: true`
 * candidates to tier 1 when `positionKind === 'from'` and at least one
 * alias is bound; outside that context they fall to tier 6 so misplaced
 * snippets cannot outrank real columns or relations.
 *
 * `existingAliases` comes from the bound alias set so the generated short
 * alias never collides with an alias the user has already typed. Snippets
 * with a candidate name that matches a bound alias's relation are skipped
 * (otherwise we'd suggest joining a table to itself via its own FK, which
 * the user almost never wants — self-joins are explicit user intent).
 *
 * `atJoinTargetSlot` discriminates a JOIN target slot (`… JOIN |` / `…
 * INNER JOIN |` / `… CROSS APPLY |`) from a bare-FROM-list slot
 * (`FROM |x` / `FROM a, |`). At the latter, the emitted ON-clause is
 * not a valid syntactic continuation, so the candidates are demoted to
 * tier 7 and have `fkJoin: false` (so the ranker's `fkJoinTier`
 * promotion does NOT lift them back up). They stay discoverable via
 * fuzzy match but no longer crowd out the plain table the user is
 * actually typing.
 */
function emitFkJoinSnippets(
  out: CompletionCandidate[],
  bindings: readonly AliasBinding[],
  tables: BuildArgs['snapshotTables'],
  fks: readonly FkEdge[],
  atJoinTargetSlot: boolean,
  defaults: MssqlDefaultSchemas,
): void {
  const existingAliases = new Set<string>(bindings.map((b) => b.alias.toLowerCase()));
  // Dedupe `${alias}|${candidateSchema}.${candidateName}|${fkName}` — spellings
  // kept, so a case-sensitive collation's `Orders` / `orders` stay two tables.
  const seen = new Set<string>();
  // Relation identity (`fkEndpointMatch.ts`): an unqualified binding is the
  // table SQL Server resolves it to (default schema, then `dbo`) — never every
  // schema's same-named table's FKs.
  const match = makeFkEndpointMatcher(tables, mssqlBindingResolver(tables, defaults));
  // One index per pass — never cached across requests (`fkEdgeIndex.ts`).
  const applicableFks = makeApplicableFksFinder(fks, match);
  for (const binding of bindings) {
    if (binding.kind === 'cte' || binding.kind === 'subquery' || binding.kind === 'derived') {
      continue;
    }
    for (const t of tables) {
      const candidate: TableMeta = {
        name: t.name,
        schema: t.schema,
        database: t.database,
        kind: (t.kind === 'view' ? 'view' : 'table') as TableMeta['kind'],
        columns: [],
      };
      const applicable = applicableFks(binding, candidate);
      for (const fk of applicable) {
        const dedupeKey = `${binding.alias.toLowerCase()}|${t.schema ?? ''}.${t.name}|${fk.name ?? `${fk.fromTable}->${fk.toTable}`}`;
        if (seen.has(dedupeKey)) continue;
        seen.add(dedupeKey);
        const snippet = generateFkJoinSnippet({
          from: binding,
          candidate,
          fk,
          existingAliases,
          engine: ENGINE,
          match,
        });
        out.push(
          atJoinTargetSlot
            ? snippet
            : { ...snippet, tier: 7, fkJoin: false },
        );
      }
    }
  }
}

/**
 * T-SQL's half of the shared MERGE-VALUES qualifier policy
 * (`resolver/mergeValuesQualified.ts`): recognise the slot, discover each
 * binding's column names through this resolver's own alias-column emitter
 * (async — MSSQL's column path round-trips the renderer cache), and hand both
 * to the shared rule.
 */
async function emitMssqlMergeValuesColumns(
  out: CompletionCandidate[],
  args: BuildArgs,
  ctx: BuildArgs['ctx'],
  input: OrchestratorResolverInput,
): Promise<void> {
  if (
    !isAtMergeValuesElement(
      args.text,
      ctx.statementRange.start,
      ctx.statementRange.end,
      ctx.offset,
      ENGINE,
    )
  ) {
    return;
  }
  const { target, sources } = partitionMergeBindings(ctx.aliasBindings);
  if (sources.length === 0) return;
  const columnNamesOf = async (binding: AliasBinding): Promise<readonly string[]> => {
    const scratch: CompletionCandidate[] = [];
    await emitAliasColumns(
      scratch, binding, args.snapshotTables, ctx.cteBindings, input, args.synthRels, args.defaultSchemas,
    );
    return scratch.map((c) => c.label);
  };
  const sourceColumns = [];
  for (const binding of sources) {
    sourceColumns.push({ binding, columns: await columnNamesOf(binding) });
  }
  emitMergeValuesQualifiedColumns({
    out,
    sources: sourceColumns,
    targetColumns: target ? await columnNamesOf(target) : [],
    formatColumn: formatIdentifier,
  });
}

async function emitAllBoundAliasColumns(
  out: CompletionCandidate[],
  bindings: readonly AliasBinding[],
  tables: BuildArgs['snapshotTables'],
  cteBindings: readonly CteBinding[],
  input: OrchestratorResolverInput,
  synthRels: readonly MssqlSyntheticRelation[],
  defaults: MssqlDefaultSchemas,
): Promise<void> {
  for (const b of bindings) {
    await emitAliasColumns(out, b, tables, cteBindings, input, synthRels, defaults);
  }
}

async function emitAliasColumns(
  out: CompletionCandidate[],
  binding: AliasBinding,
  tables: BuildArgs['snapshotTables'],
  cteBindings: readonly CteBinding[],
  input: OrchestratorResolverInput,
  synthRels: readonly MssqlSyntheticRelation[],
  defaults: MssqlDefaultSchemas,
): Promise<void> {
  if (binding.kind === 'subquery' || binding.kind === 'derived') {
    // OPENJSON aliases come back as kind='derived' (the relation parser
    // sees the `(args)` parens and tags it derived). Match by alias name
    // against scanned OPENJSON aliases.
    const synth = synthRels.find(
      (r) => r.kind === 'openjson' && r.name.toLowerCase() === binding.alias.toLowerCase(),
    );
    if (synth) {
      emitSyntheticColumns(out, synth, binding.alias);
      return;
    }
    // Derived-table column inference — parse the inner SELECT's projection
    // and surface its columns. Works against the schema-bus snapshot (which
    // for MSSQL carries `{name, schema}` plus, when populated, column info
    // from the renderer-side cache or the test seam).
    if (binding.bodyRange) {
      const text = input.model.getValue();
      let inferred = inferSubqueryColumns({
        source: text,
        bodyRange: binding.bodyRange,
        engineId: 'mssql',
        snapshotTables: tables.map((t) => ({
          name: t.name,
          ...(t.schema !== undefined ? { schema: t.schema } : {}),
          columns: (t.columns ?? []).map((c) => ({ name: c.name })),
        })),
        cteBindings,
      });
      // Post-PIVOT shape: an outer `pvt.|` reference resolves to the pivoted
      // columns — the FOR + aggregate-argument columns are consumed and the
      // value aliases (`[2011]`, …) are added. The interior caret is served
      // earlier in `buildCandidates`, so reaching here is an OUTER reference.
      if (binding.pivotRange) {
        const parsed = parsePivotClause(
          text.slice(binding.pivotRange.start, binding.pivotRange.end),
        );
        if (parsed) inferred = applyPivotTransform(inferred, parsed);
      }
      for (const name of inferred) {
        out.push({
          label: name,
          insertText: name,
          isSnippet: false,
          kind: 'column',
          tier: 1,
          detail: `(${binding.alias})`,
        });
      }
    }
    return;
  }
  if (binding.kind === 'cte') {
    // The aliasScope binding's `relation` is the CTE's name (case-preserving),
    // so we look up the matching CTE binding for its explicit column list.
    const relationLower = unquoteIdentifier(binding.relation).toLowerCase();
    const cte = cteBindings.find((c) => c.name.toLowerCase() === relationLower);
    if (!cte) return;
    emitCteColumnList(
      out,
      cte,
      {
        source: input.model.getValue(),
        engineId: 'mssql',
        snapshotTables: tables,
        cteBindings,
      },
      binding.alias,
    );
    return;
  }
  // Table-variable / temp-table relation match.
  if (binding.relation.startsWith('@') || binding.relation.startsWith('#')) {
    const synth = findSyntheticRelationByBase(synthRels, binding.relation.toLowerCase());
    if (synth) emitSyntheticColumns(out, synth, binding.alias);
    return;
  }
  // Find the matching table in the snapshot — an unqualified binding the way
  // SQL Server resolves it (default schema, then `dbo`), never whichever
  // same-named table the snapshot lists first.
  const relationLower = unquoteIdentifier(binding.relation).toLowerCase();
  const schemaLower = binding.schema ? unquoteIdentifier(binding.schema).toLowerCase() : null;
  const tbl = findMssqlTable(tables, schemaLower, relationLower, defaults);
  if (!tbl) {
    // System-catalog fallback — the snapshot doesn't include `sys.*` or
    // `INFORMATION_SCHEMA.*` (their volume would dwarf user-table metadata),
    // but when the binding's schema points at one of those catalogs and the
    // relation name matches a known catalog table, we project columns from
    // the static dictionary. Closes mssql script.13.§1, script.26.§1.
    if (schemaLower) {
      const sysCols = getSystemCatalogColumns(schemaLower, relationLower);
      if (sysCols) {
        emitSystemCatalogColumns(
          out,
          sysCols,
          `${schemaLower}.${relationLower}`,
          binding.alias,
        );
      }
    }
    return;
  }
  // In-buffer CREATE TABLE entries carry their parsed columns inline (they
  // aren't in the live column cache yet, which `ensureColumnsForTable` reads).
  // Live bus tables load columns lazily and leave `tbl.columns` empty, so this
  // branch only fires for buffer-local relations. Flips mssql script 42 §8.
  if (tbl.columns && tbl.columns.length > 0) {
    for (const c of tbl.columns) {
      out.push({
        label: c.name,
        insertText: c.name,
        isSnippet: false,
        kind: 'column',
        tier: 1,
        detail: `${c.dataType} (${binding.alias})`,
      });
    }
    return;
  }
  if (!input.connectionId || !input.database) return;

  const cols = await ensureColumnsForTable(
    input.connectionId,
    input.database,
    tbl.schema ?? 'dbo',
    tbl.name,
  );
  for (const c of cols) {
    out.push({
      label: c.name,
      insertText: c.name,
      isSnippet: false,
      kind: 'column',
      tier: 1,
      detail: `${c.dataType} (${binding.alias})`,
    });
  }
}

function findSyntheticRelationByBase(
  rels: readonly MssqlSyntheticRelation[],
  baseLower: string,
): MssqlSyntheticRelation | undefined {
  return rels.find((r) => r.name.toLowerCase() === baseLower);
}

function emitSyntheticRelationsAsCandidates(
  out: CompletionCandidate[],
  rels: readonly MssqlSyntheticRelation[],
  statementRange: { readonly start: number; readonly end: number },
  opts?: {
    /** When set, the synthetic relation declared NEAREST before this offset
     *  is emitted at tier 1 instead of 2 — the just-created #temp / @tablevar
     *  is the likeliest INSERT target (script.45 §2/§9). */
    readonly promoteNearestBefore?: number;
  },
): void {
  const promoteAt = opts?.promoteNearestBefore;
  let nearest: MssqlSyntheticRelation | undefined;
  if (promoteAt !== undefined) {
    for (const r of rels) {
      if (r.kind === 'openjson' || r.definedAt.start >= promoteAt) continue;
      if (!nearest || r.definedAt.start > nearest.definedAt.start) nearest = r;
    }
  }
  for (const r of rels) {
    // OPENJSON aliases aren't "tables you can FROM" — they only matter
    // inline; skip them at this position.
    if (r.kind === 'openjson') continue;
    // Temp tables / table variables: visible script-wide.
    out.push({
      label: r.name,
      insertText: r.name,
      isSnippet: false,
      kind: 'table',
      tier: r === nearest ? 1 : 2,
      detail: r.kind === 'table-variable' ? 'Table variable' : 'Temp table',
      filterText: r.name,
    });
  }
  void statementRange;
}

/**
 * `inserted` / `deleted` as RELATION candidates — the two pseudo-tables a
 * T-SQL trigger body reads. Only emitted when the caret is inside a trigger
 * body (the caller gates on the host table), because outside one the names are
 * ordinary identifiers.
 *
 * The dotted-access path already resolves `inserted.▮` to the host table's
 * columns; this is the FROM-position half of the same pair (script.36
 * §2/§7/§8), which had nothing at all before.
 */
function emitTriggerPseudoRelations(
  out: CompletionCandidate[],
  host: MssqlTriggerHostTable,
): void {
  const hostLabel = host.schema ? `${host.schema}.${host.name}` : host.name;
  for (const [index, name] of (['inserted', 'deleted'] as const).entries()) {
    out.push({
      label: name,
      insertText: name,
      isSnippet: false,
      kind: 'table',
      tier: 1,
      detail: `Trigger pseudo-table (${hostLabel} row image)`,
      filterText: name,
      // The pair leads the tier as a GROUP. Tier 1 alone is not enough: the
      // trigger's own host table and the body's INSERT target are
      // buffer-referenced, so they share tier 1 and interleave alphabetically
      // between `deleted` and `inserted` (script.36 §7/§8 assert the two
      // pseudo-tables occupy the head). The caret's own binding still leads
      // via the ranker's `leader` key, which is compared before orderHint.
      orderHint: index,
      // Two engine-curated names sharing the unbounded 'table' kind — exempt
      // from the ranker's untyped cap, like the other curated relation sets.
      capExempt: true,
    });
  }
}

/**
 * Synthetic relations visible at the cursor:
 *   * `table-variable` / `temp-table` are script-level — visible anywhere.
 *   * `openjson` aliases are statement-local — visible only when their
 *     declaration falls inside the current statement.
 */
function visibleSynthRels(
  rels: readonly MssqlSyntheticRelation[],
  statementRange: { readonly start: number; readonly end: number },
): readonly MssqlSyntheticRelation[] {
  return rels.filter((r) => {
    if (r.kind !== 'openjson') return true;
    return (
      r.definedAt.start >= statementRange.start &&
      r.definedAt.end <= statementRange.end
    );
  });
}

function emitSyntheticColumns(
  out: CompletionCandidate[],
  rel: MssqlSyntheticRelation,
  aliasName?: string,
): void {
  const ctxLabel = aliasName ?? rel.name;
  const kindLabel = rel.kind === 'table-variable'
    ? 'Table variable'
    : rel.kind === 'temp-table'
      ? 'Temp table'
      : 'OPENJSON';
  for (const col of rel.columns) {
    const dataType = rel.columnTypes.get(col.toLowerCase());
    const detail = dataType
      ? `${dataType} (${kindLabel} ${ctxLabel})`
      : `${kindLabel} ${ctxLabel}`;
    out.push({
      label: col,
      insertText: col,
      isSnippet: false,
      kind: 'column',
      tier: 1,
      detail,
    });
  }
}

/**
 * Surface the columns of a MERGE / OUTPUT-DML target as `inserted.col`
 * or `deleted.col` candidates. The columns come from the same
 * `mssqlColumnCache` consulted for regular alias-dot — the per-table
 * cache is keyed by `(connection, database, schema, table)` so the
 * lookup is cheap once the table is warm. Cold cache returns an empty
 * list; Monaco re-fetches on the next keystroke per the same
 * fire-and-forget contract documented in the resolver header.
 */
async function emitDmlTargetColumns(
  out: CompletionCandidate[],
  target: MssqlMergeOrOutputTarget,
  tables: BuildArgs['snapshotTables'],
  pseudo: 'inserted' | 'deleted',
  input: OrchestratorResolverInput,
  defaults: MssqlDefaultSchemas,
): Promise<void> {
  if (!input.connectionId || !input.database) return;
  const tbl = findMssqlTable(tables, target.schema ?? null, target.name, defaults);
  if (!tbl) return;
  const cols = await ensureColumnsForTable(
    input.connectionId,
    input.database,
    tbl.schema ?? 'dbo',
    tbl.name,
  );
  for (const c of cols) {
    out.push({
      label: c.name,
      insertText: c.name,
      isSnippet: false,
      kind: 'column',
      tier: 1,
      detail: `${c.dataType} (${pseudo})`,
    });
  }
  // `$action` is a MERGE-only OUTPUT pseudo-column ('INSERT' / 'UPDATE' /
  // 'DELETE'). It is NOT valid in INSERT / UPDATE / DELETE OUTPUT clauses.
  // Emit alongside `inserted.` / `deleted.` column lists when the enclosing
  // statement is a MERGE — matches the spec's "user typing inside OUTPUT
  // sees `$action` as a relevant suggestion" intent.
  if (target.statementKind === 'merge') {
    out.push({
      label: '$action',
      insertText: '$action',
      isSnippet: false,
      kind: 'column',
      tier: 1,
      detail: 'MERGE OUTPUT pseudo-column (INSERT / UPDATE / DELETE)',
    });
  }
}

/**
 * Emit columns from a CTE's explicit column list. When no explicit list is
 * declared (`WITH cte AS (…)` without parens), nothing is emitted — implicit
 * column inference from the CTE body is deferred to a later iteration.
 *
 * `aliasOrCteName` defaults to the CTE's own name and may be overridden when
 * the cursor's alias is a separately-named binding (e.g. `USING cte s`).
 */
type CteResolveArgs = {
  readonly source: string;
  readonly engineId: DatabaseEngineId;
  readonly snapshotTables: BuildArgs['snapshotTables'];
  readonly cteBindings: readonly CteBinding[];
};

function emitCteColumnList(
  out: CompletionCandidate[],
  cte: CteBinding,
  resolveArgs: CteResolveArgs,
  aliasOrCteName?: string,
): void {
  const cols = resolveCteColumns({
    cte,
    source: resolveArgs.source,
    engineId: resolveArgs.engineId,
    snapshotTables: resolveArgs.snapshotTables.map((t) => ({
      name: t.name,
      ...(t.schema !== undefined ? { schema: t.schema } : {}),
      columns: (t.columns ?? []).map((c) => ({ name: c.name })),
    })),
    cteBindings: resolveArgs.cteBindings,
  });
  if (cols.length === 0) return;
  const detailContext = aliasOrCteName ?? cte.name;
  for (const col of cols) {
    out.push({
      label: col,
      insertText: col,
      isSnippet: false,
      kind: 'column',
      tier: 1,
      detail: `Column (${detailContext})`,
    });
  }
}

async function ensureColumnsForTable(
  connectionId: string,
  database: string,
  schema: string,
  table: string,
): Promise<readonly MssqlColumnEntry[]> {
  const cached = getCachedMssqlColumns(connectionId, database, schema, table);
  if (cached !== undefined) return cached;
  // Cold / stale: the caller now waits on the IPC fetch (new or in flight) —
  // real I/O for the provider-cost measurement (`providerCost.ts`).
  noteCompletionIoWait();
  return ensureMssqlColumns(connectionId, database, schema, table);
}

async function emitTriggerHostColumns(
  out: CompletionCandidate[],
  host: MssqlTriggerHostTable,
  tables: BuildArgs['snapshotTables'],
  pseudo: 'inserted' | 'deleted',
  input: OrchestratorResolverInput,
  defaults: MssqlDefaultSchemas,
): Promise<void> {
  if (!input.connectionId || !input.database) return;
  const tbl = findMssqlTable(tables, host.schema ?? null, host.name, defaults);
  if (!tbl) return;
  const cols = await ensureColumnsForTable(
    input.connectionId,
    input.database,
    tbl.schema ?? 'dbo',
    tbl.name,
  );
  // PK first. The pseudo-table's column set is the host table's, and at the
  // `ON d.▮ = i.OrderItemID` caret the key column is what the join is written
  // against — with every column at one flat tier the ranker fell back to
  // alphabetical and buried it (script.36 §9). The flag lives on the bus
  // snapshot's ColumnMeta; the lazily-introspected cache has no PK detail, so
  // a snapshot without columns keeps the old flat order.
  const pkNames = new Set(
    tbl.columns.filter((c) => c.isPrimaryKey === true).map((c) => c.name.toLowerCase()),
  );
  for (const c of cols) {
    const isPk = pkNames.has(c.name.toLowerCase());
    out.push({
      label: c.name,
      insertText: c.name,
      isSnippet: false,
      kind: 'column',
      tier: pkNames.size > 0 && !isPk ? 2 : 1,
      detail: isPk
        ? `${c.dataType} (PK, ${pseudo} → ${tbl.schema ?? ''}.${tbl.name})`
        : `${c.dataType} (${pseudo} → ${tbl.schema ?? ''}.${tbl.name})`,
    });
  }
}

// ───────────────────────────────────────────────────────────────────────────
// GRA-8 S3 — DDL constraint/reference column-list emission.
//
// These mirror the SQLite resolver's `findReferencesTargetTable` /
// `extractCreateTableInProgressColumns` shapes, re-expressed for MSSQL's
// multi-schema, bracket-quoted identifier model and its PK-first ranking.
// ───────────────────────────────────────────────────────────────────────────

/** Match a `REFERENCES <schema?>.<table>(` segment; capture schema + table. */
const MSSQL_REFERENCES_TARGET_RE =
  /\bREFERENCES\s+(?:(\[[^\]]*\]|"(?:[^"]|"")*"|[A-Za-z_@#][\w$@#]*)\s*\.\s*)?(\[[^\]]*\]|"(?:[^"]|"")*"|[A-Za-z_@#][\w$@#]*)\s*\(/gi;

/** Match an `ALTER TABLE <schema?>.<table>` header; capture schema + table. */
const MSSQL_ALTER_TABLE_TARGET_RE =
  /\bALTER\s+TABLE\s+(?:(\[[^\]]*\]|"(?:[^"]|"")*"|[A-Za-z_@#][\w$@#]*)\s*\.\s*)?(\[[^\]]*\]|"(?:[^"]|"")*"|[A-Za-z_@#][\w$@#]*)/gi;

/** Extract the LAST `REFERENCES <tbl>(` target before the cursor. */
function findReferencesTargetForMssql(
  text: string,
  statementStart: number,
  cursorOffset: number,
): { schema: string | null; table: string } | null {
  return lastMatchTarget(
    text.slice(statementStart, cursorOffset),
    MSSQL_REFERENCES_TARGET_RE,
  );
}

/** Extract the enclosing `ALTER TABLE <tbl>` target before the cursor. */
function findAlterTableTargetForMssql(
  text: string,
  statementStart: number,
  cursorOffset: number,
): { schema: string | null; table: string } | null {
  return lastMatchTarget(
    text.slice(statementStart, cursorOffset),
    MSSQL_ALTER_TABLE_TARGET_RE,
  );
}

function lastMatchTarget(
  slice: string,
  re: RegExp,
): { schema: string | null; table: string } | null {
  re.lastIndex = 0;
  let last: RegExpExecArray | null = null;
  let m: RegExpExecArray | null = null;
  while ((m = re.exec(slice)) !== null) last = m;
  if (!last || !last[2]) return null;
  return {
    schema: last[1] ? unquoteIdentifier(last[1]) : null,
    table: unquoteIdentifier(last[2]),
  };
}

/** The table a relation reference (`[sales].[orders]` spelling) names, as SQL Server resolves it. */
function mssqlTableResolver<T extends { readonly name: string; readonly schema?: string }>(
  tables: readonly T[],
  defaults: MssqlDefaultSchemas,
): RelationResolver<T> {
  return (name, schema) =>
    findMssqlTable(tables, schema ? unquoteIdentifier(schema).toLowerCase() : null, unquoteIdentifier(name).toLowerCase(), defaults);
}

/** The table a bound relation names, as SQL Server resolves it — FK / loose-FK JOIN identity. */
function mssqlBindingResolver<T extends { readonly name: string; readonly schema?: string }>(
  tables: readonly T[],
  defaults: MssqlDefaultSchemas,
): (binding: AliasBinding) => T | undefined {
  const resolve = mssqlTableResolver(tables, defaults);
  return (b) => resolve(b.relation, b.schema || undefined);
}

/** Find a snapshot table by (optional) schema + name, case-insensitively — unqualified as SQL Server resolves it. */
function findSnapshotTable(
  args: BuildArgs,
  schema: string | null,
  table: string,
): BuildArgs['snapshotTables'][number] | undefined {
  return findMssqlTable(args.snapshotTables, schema, table, args.defaultSchemas, eqIdentifier);
}

/**
 * Workstream C parity — lower-cased JSON document columns of the aliased table:
 * columns the buffer already uses as a JSON_* first arg ∪ bus-classified ∪
 * MetaInfo-declared. Feeds {@link applyJsonFunctionArgPromotion} at a
 * `JSON_VALUE(o.▮` first-arg slot. Mirrors oracle's `collectKnownJsonColumns`.
 */
function collectMssqlKnownJsonColumns(args: BuildArgs, binding: AliasBinding): Set<string> {
  const cols = collectBufferJsonFunctionColumns(args.text);
  const relation = unquoteIdentifier(binding.relation);
  if (relation.startsWith('@') || relation.startsWith('#')) return cols;
  const schema = binding.schema ? unquoteIdentifier(binding.schema) : null;
  const tbl = findSnapshotTable(args, schema, relation);
  if (!tbl) return cols;
  const tblSchema = tbl.schema ?? '';
  const { connectionId, database } = args.input;
  for (const c of tbl.columns) {
    const busJson = !!c.jsonClassification && c.jsonClassification.kind !== 'not-json';
    const declared =
      !busJson &&
      !!connectionId &&
      !!database &&
      isJsonColumnDeclared(connectionId, database, tblSchema, tbl.name, c.name);
    if (busJson || declared) cols.add(c.name.toLowerCase());
  }
  return cols;
}

/**
 * Emit the REFERENCED table's columns at a `REFERENCES tbl(▮` slot, with the
 * primary-key column(s) ranked first (tier 1), the rest at tier 2. Columns
 * come from the bus snapshot (which carries `isPrimaryKey`); when the snapshot
 * has no column detail, we fall back to the lazily-introspected cache (no PK
 * flag there — everything lands at tier 2).
 */
function emitReferencesColumnList(out: CompletionCandidate[], args: BuildArgs): void {
  const target = findReferencesTargetForMssql(
    args.text,
    args.ctx.statementRange.start,
    args.ctx.offset,
  );
  if (!target) return;
  emitTargetTableColumnsPkFirst(out, args, target.schema, target.table);
}

/** Match a `CREATE … INDEX … ON <schema?>.<table>` header; capture schema + table. */
const MSSQL_INDEX_TARGET_RE =
  /\bON\s+(?:(\[[^\]]*\]|"(?:[^"]|"")*"|[A-Za-z_@#][\w$@#]*)\s*\.\s*)?(\[[^\]]*\]|"(?:[^"]|"")*"|[A-Za-z_@#][\w$@#]*)\s*\(/gi;

/**
 * Emit the indexed table's columns at a `CREATE INDEX … ON tbl (▮` key-column
 * or `INCLUDE (▮` covering-column caret, PK first. Snapshot columns are
 * preferred (they carry the PK flag); a snapshot without column detail falls
 * back to the lazily-introspected cache, exactly like the constraint slots.
 */
async function emitIndexColumnList(
  out: CompletionCandidate[],
  args: BuildArgs,
): Promise<void> {
  const target = lastMatchTarget(
    args.text.slice(args.ctx.statementRange.start, args.ctx.offset),
    MSSQL_INDEX_TARGET_RE,
  );
  if (!target) return;
  emitTargetTableColumnsPkFirst(out, args, target.schema, target.table);
  if (out.length === 0) {
    await emitTargetTableColumnsFromCache(out, args, target.schema, target.table);
  }
}

/**
 * Emit the ALTER TABLE target's live columns at a `FK/PK/UNIQUE (▮` constraint
 * column-list slot. The altered table exists, so its columns come straight from
 * the snapshot (PK first), with a lazy-cache fallback.
 */
async function emitAlterTableConstraintColumns(
  out: CompletionCandidate[],
  args: BuildArgs,
): Promise<void> {
  const target = findAlterTableTargetForMssql(
    args.text,
    args.ctx.statementRange.start,
    args.ctx.offset,
  );
  if (!target) return;
  emitTargetTableColumnsPkFirst(out, args, target.schema, target.table);
  // When the snapshot carries no column detail, kick the lazy introspection so
  // the next keystroke renders columns (mirrors emitDmlTargetColumns).
  if (out.length === 0) await emitTargetTableColumnsFromCache(out, args, target.schema, target.table);
}

function emitTargetTableColumnsPkFirst(
  out: CompletionCandidate[],
  args: BuildArgs,
  schema: string | null,
  table: string,
): void {
  const tbl = findSnapshotTable(args, schema, table);
  if (!tbl || tbl.columns.length === 0) return;
  for (const c of tbl.columns) {
    out.push({
      label: c.name,
      insertText: formatIdentifier(c.name),
      isSnippet: false,
      kind: 'column',
      tier: c.isPrimaryKey ? 1 : 2,
      detail: c.isPrimaryKey ? `${c.dataType} (PK)` : c.dataType,
      filterText: c.name,
    });
  }
}

async function emitTargetTableColumnsFromCache(
  out: CompletionCandidate[],
  args: BuildArgs,
  schema: string | null,
  table: string,
): Promise<void> {
  const { input } = args;
  if (!input.connectionId || !input.database) return;
  const cols = await ensureColumnsForTable(
    input.connectionId,
    input.database,
    schema ?? 'dbo',
    table,
  );
  for (const c of cols) {
    out.push({
      label: c.name,
      insertText: formatIdentifier(c.name),
      isSnippet: false,
      kind: 'column',
      tier: 2,
      detail: c.dataType,
      filterText: c.name,
    });
  }
}

/**
 * Emit the columns declared SO FAR in a CREATE TABLE statement at a CHECK /
 * PK / UNIQUE / FK column-list slot — the new table doesn't exist in the
 * schema yet, so its in-progress columns are parsed from the CREATE TABLE
 * preamble up to the cursor. Top-level segments leading with a table-constraint
 * keyword (CONSTRAINT / PRIMARY / UNIQUE / FOREIGN / CHECK) are skipped.
 */
function emitCreateTableInProgressColumns(out: CompletionCandidate[], args: BuildArgs): void {
  const cols = extractMssqlCreateTableInProgressColumns(
    args.text,
    args.ctx.statementRange.start,
    args.ctx.offset,
  );
  for (const col of cols) {
    out.push({
      label: col,
      insertText: formatIdentifier(col),
      isSnippet: false,
      kind: 'column',
      tier: 1,
      detail: 'In-progress column',
      filterText: col,
    });
  }
}

const MSSQL_CREATE_TABLE_HEADER_RE =
  /\bCREATE\s+TABLE\s+(?:\[[^\]]*\]|"(?:[^"]|"")*"|[A-Za-z_@#][\w$@#]*(?:\s*\.\s*(?:\[[^\]]*\]|"(?:[^"]|"")*"|[A-Za-z_@#][\w$@#]*))*)\s*\(/i;

const MSSQL_FIRST_IDENT_RE =
  /^\s*(?:\[([^\]]*)\]|"((?:[^"]|"")*)"|([A-Za-z_@#][\w$@#]*))/;

const MSSQL_TABLE_CONSTRAINT_LEAD = new Set([
  'constraint',
  'primary',
  'unique',
  'foreign',
  'check',
  'index',
  'period',
]);

function extractMssqlCreateTableInProgressColumns(
  text: string,
  statementStart: number,
  cursorOffset: number,
): readonly string[] {
  const slice = text.slice(statementStart, cursorOffset);
  const m = MSSQL_CREATE_TABLE_HEADER_RE.exec(slice);
  if (!m) return [];
  const body = slice.slice(m.index + m[0].length);
  const segments: string[] = [];
  let depth = 0;
  let segStart = 0;
  for (let i = 0; i < body.length; i += 1) {
    const ch = body[i]!;
    if (ch === '(') depth += 1;
    else if (ch === ')') depth -= 1;
    else if (ch === ',' && depth === 0) {
      segments.push(body.slice(segStart, i));
      segStart = i + 1;
    }
  }
  segments.push(body.slice(segStart));

  const result: string[] = [];
  const seen = new Set<string>();
  for (const seg of segments) {
    const im = MSSQL_FIRST_IDENT_RE.exec(seg);
    if (!im) continue;
    const raw = im[1] ?? im[2] ?? im[3] ?? '';
    if (!raw) continue;
    if (MSSQL_TABLE_CONSTRAINT_LEAD.has(raw.toLowerCase())) continue;
    const key = raw.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(raw);
  }
  return result;
}

/**
 * Inside `BEGIN CATCH` the error-state intrinsics ARE the default: they share
 * tier 1 with the batch's `@locals`, and without an order hint the `@` sigil
 * sorted every local ahead of them (mssql script.40 §7). `orderHint` by
 * curated position keeps `ERROR_MESSAGE` / `ERROR_NUMBER` first. mssql-only
 * vocabulary — no other engine emits CATCH-block intrinsics.
 */
function emitCatchErrorFunctions(out: CompletionCandidate[]): void {
  MSSQL_CATCH_ERROR_FUNCTIONS.forEach((fn, index) => {
    out.push({
      label: fn.name,
      insertText: fn.snippetInsertText,
      isSnippet: false,
      kind: 'system-function',
      tier: 1,
      detail: fn.detail,
      filterText: fn.name,
      orderHint: index,
    });
  });
}

// Small helper for tests that bypass keyword emission.
export function __testEmitTablesAndSchemas(
  tables: BuildArgs['snapshotTables'],
  schemas: readonly string[],
): CompletionCandidate[] {
  const out: CompletionCandidate[] = [];
  emitTablesAndSchemas(out, tables, schemas);
  return out;
}

export function __testCheckEqIdentifier(a: string, b: string): boolean {
  return eqIdentifier(a, b);
}

